import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  FiX, FiSearch, FiMinus, FiPlus, FiTrash2,
  FiPercent, FiRotateCcw, FiUser, FiChevronDown, FiList,
  FiPause, FiPlay, FiRepeat,
  FiCamera, FiShoppingCart, FiChevronLeft, FiMoreHorizontal
} from 'react-icons/fi';
import POSCameraScanner from './POSCameraScanner';
import { colors, borderRadius, typography, shadows } from '../../theme/colors';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { createLinkedAccount, ensureParentAccountExists } from '../../utils/coaInitializer';
import { useAuth } from '../../context/AuthContext';
import { loadIfEnabled as loadCustomerPricing, EMPTY_PRICING, filterItemsByPricing, getOverridePrice } from '../../utils/customerPricingHelper';
import { checkStockAvailability, toBaseQty } from '../../utils/validationHelpers';
import { secondUnitForLine, secondQtyFromQty, qtyFromSecondQty, twoUnitSwitchQty, twoUnitLineFields, formatTwoUnitQty } from '../../utils/unitConversion';
import useCurrency from '../../hooks/useCurrency';
import {
  ensureWalkInCustomer,
  calculatePOSLine,
  calculatePOSTotals,
  lookupPOSReceipt,
  getStockBalancesCached,
  applyPersistedStockDelta,
  withReturnBasis,
  receiptPayFactor
} from '../../utils/posHelper';
import { preloadPOSCaches, allocatePOSDocNumber, preparePOSSale, savePreparedPOSSale } from '../../utils/posSaveRouter';
import { syncMasterDataToLocal } from '../../utils/posMasterSync';
import { getItemsCached, getCustomersCached, getAccountsCached } from '../../utils/masterDataStore';
import { invalidateCache } from '../../utils/fyDataCache';
import { isOnline, onConnectivityChange } from '../../utils/connectivity';
import { ensureRangeAllocated } from '../../utils/posDocCounterRange';
import { getOpenShift, openShift as openShiftDoc, closeShift as closeShiftDoc, getShiftTotals, getCashAccountBalance, cacheOpenShift, readCachedOpenShift } from '../../utils/posShiftHelper';
import { countPendingForShift, listPendingForShift, discardOutboxRows } from '../../utils/posLocalCache';
import { drainOutbox } from '../../utils/posSyncWorker';
import { printZReport } from './POSZReport';
import POSOfflineIndicator from '../../components/POSOfflineIndicator';
import { updateTransactionStatus, deletePOSBillWithPayments } from '../../utils/transactionHelper';
import { verifyAdminPassword } from '../../utils/verifyAdminPassword';
import { deleteSubTransactions } from '../../utils/subTransactionHelper';
import { deleteStockFlow, applyEntriesToStockCache } from '../../utils/stockFlowHelper';
import { parsePOSInput, findItemByTerm } from '../../utils/posSmartInput';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import usePOSKeyboard from '../../hooks/usePOSKeyboard';
import usePagePermissions from '../../hooks/usePagePermissions';
import POSProductGrid from './POSProductGrid';
import PaymentBar from './PaymentBar';
import POSHistoryModal from './POSHistoryModal';
import POSCalculatorModal from './POSCalculatorModal';
import POSReceiptPickerModal from './POSReceiptPickerModal';
import { printReceipt } from './POSReceipt';
import { getCustomerBalance } from '../../utils/liveBalanceHelper';
import { assertPosLocationUnlocked, isLocationLocked } from '../../utils/stockAuditHelper';
import WeightScaleWidget from '../../components/pos/WeightScaleWidget';
import { localDateStr } from '../../utils/localDate';
import {
  BANK_DISCOUNT_MODULE_ID, loadBankDiscountOffers, offersValidNow, offerUnavailableReason,
  cardMatchesOffer, readCardUsage, checkCardLimits, maxDiscountForBill, splitShares,
  maskCard, offerLabel, reverseBankDiscountUsage
} from '../../utils/bankDiscountHelper';

// Phone is the unique key for POS quick-add — compare on digits only.
const normalizePhone = (s) => String(s || '').replace(/\D/g, '');

// Return lines from a bank-discounted sale: carry each line's bank-discount per
// unit (so a partial return gives back the right share) and turn the line's
// discount into a % so it scales with the returned quantity. Lines without a
// bank discount are returned exactly as before (this returns {}).
const bankReturnLineFields = (line) => {
  const bd = Number(line.bankDiscountAmount) || 0;
  const qty = Number(line.quantity) || 0;
  const gross = qty * (Number(line.unitPrice) || 0);
  if (!(bd > 0) || !(qty > 0) || !(gross > 0)) return {};
  return {
    _bankDiscPerUnit: bd / qty,
    discountPercent: Math.round(((Number(line.discountAmount) || 0) / gross) * 100 * 10000) / 10000,
    discountAmount: 0
  };
};
// What a return needs to know about the original sale's bank discount.
const bankReturnStash = (original) => (original?.bankDiscount
  ? { ...original.bankDiscount, transactionId: original.id, docNumber: original.docNumber }
  : null);

const POSScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Counter this POS session is bound to (from the Sales > POS menu link).
  const counterId = useMemo(() => new URLSearchParams(location.search).get('counter') || null, [location.search]);
  const { formatCurrency } = useCurrency();
  const { activeCompany, activeFinancialYear, currentUser, userProfile, companyUser, activeBranch, isModuleEnabled, hasPermission } = useAuth();
  const multiUnitEnabled = isModuleEnabled && isModuleEnabled('master_units');
  const { canEdit } = usePagePermissions('transactions', 'posCounterSale');
  // Editing a line's unit price needs an explicit right (owner/admin bypass).
  // Cashiers without it sell only at the set price.
  const canEditPrice = hasPermission ? hasPermission('transactions', 'posPriceEdit', 'edit') : true;
  // POS-01: the server refuses any save from a member switched off in Users &
  // Roles (the company owner is never blocked) — catch it before printing.
  const posUserInactive = !!(companyUser && companyUser.isActive === false
    && !(activeCompany?.ownerId && currentUser?.uid && activeCompany.ownerId === currentUser.uid));
  const companyId = activeCompany?.id;
  const searchInputRef = useRef(null);

  // ── Core State ──
  const [cart, setCart] = useState([]);
  const [selectedCartIndex, setSelectedCartIndex] = useState(-1);
  const [returnMode, setReturnMode] = useState(false);
  const [returnOriginalId, setReturnOriginalId] = useState(null);
  const [returnOriginalDoc, setReturnOriginalDoc] = useState('');
  const [headerDiscount, setHeaderDiscount] = useState(0);

  // ── Bank card discounts (module pos_bank_discounts) ──
  // Offers load once per POS session, only when the module is ON. The applied
  // discount is NOT written into the cart — it is passed to the totals engine,
  // which spreads it across the lines before GST. Any cart change drops it
  // (the cashier re-applies) so limits checked at apply time stay true.
  const bankDiscountsEnabled = !!(isModuleEnabled && isModuleEnabled(BANK_DISCOUNT_MODULE_ID));
  const [bankOffers, setBankOffers] = useState([]);
  const [bankDiscount, setBankDiscount] = useState(null);
  const bankDiscountRef = useRef(null);
  useEffect(() => { bankDiscountRef.current = bankDiscount; }, [bankDiscount]);
  // Original sale's bank discount while a RETURN against it is on screen.
  const returnBankDiscRef = useRef(null);
  const [posOnline, setPosOnline] = useState(() => isOnline());
  useEffect(() => onConnectivityChange((on) => setPosOnline(!!on)), []);

  // ── Price Type (Retail / Wholesale / Dealer) — gated by pos_price_type ──
  // A ref mirrors the state so addToCart can read the latest without being
  // re-created (it's passed to the product grid / scanner). Switching the type
  // only affects items added AFTERWARDS — lines already in the cart keep price.
  const priceTypeEnabled = isModuleEnabled('pos_price_type');
  const [priceType, setPriceType] = useState('Retail');
  const priceTypeRef = useRef('Retail');
  useEffect(() => { priceTypeRef.current = priceType; }, [priceType]);

  // ── Exchange Mode (return + new sale in one transaction) ──
  // When true, the screen shows a return panel above the cart with per-line
  // checkboxes and an editable return qty; the regular cart below collects
  // the new items to buy. Pay computes net (refund or extra charge).
  const [exchangeMode, setExchangeMode] = useState(false);
  const [exchangeOriginalId, setExchangeOriginalId] = useState(null);
  const [exchangeOriginalDoc, setExchangeOriginalDoc] = useState('');
  // exchangeReturnLines: same shape as original lines + { _selected, returnQty }
  const [exchangeReturnLines, setExchangeReturnLines] = useState([]);
  const [exchangeReason, setExchangeReason] = useState('Customer Change');

  // ── Customer ──
  const [walkInCustomer, setWalkInCustomer] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [customerPricing, setCustomerPricing] = useState(EMPTY_PRICING);
  const [customerName, setCustomerName] = useState('Walk-in Customer');
  const [customers, setCustomers] = useState([]);
  // Outstanding balance BEFORE this bill — only fetched when the receipt's
  // "Show Previous Balance" toggle is on and a named (non-walk-in) customer is set.
  const [customerBalance, setCustomerBalance] = useState(null);
  // Stock Audit freeze on this POS's location (shows a banner; checkout is blocked).
  const [auditLocked, setAuditLocked] = useState(false);
  // Mobile-responsive POS: step flow (products → cart → pay), the More sheet, and
  // the camera scanner. These ONLY affect layout below the phone breakpoint; the
  // desktop 3-panel view ignores them entirely.
  const [mobileStep, setMobileStep] = useState('products'); // 'products' | 'cart' | 'pay'
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // ── Active POS counter (loaded from ?counter= in the URL) ──
  const [activeCounter, setActiveCounter] = useState(null);

  // ── Shift state ──
  const [activeShift, setActiveShift] = useState(null);
  const [shiftChecked, setShiftChecked] = useState(false); // have we resolved open-shift yet?
  const [showOpenShift, setShowOpenShift] = useState(false);
  const [openingCashInput, setOpeningCashInput] = useState('');
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [closeTotals, setCloseTotals] = useState(null);
  const [countedCashInput, setCountedCashInput] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [shiftBusy, setShiftBusy] = useState(false);

  // ── Data ──
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [stockBalances, setStockBalances] = useState({});
  const [paymentAccounts, setPaymentAccounts] = useState([]);

  // itemId → item record. Built once per items load and reused for (a) base-unit
  // conversion when adjusting the badge map and (b) the checkout availability
  // check, which would otherwise re-read every cart item's record from Firestore.
  const itemsById = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.id] = i; });
    return map;
  }, [items]);

  // Scope of the badge map — MUST stay identical to the arguments passed to
  // getStockBalancesCached below, or the persisted copy would be read under one
  // key and patched under another.
  const stockScopeBranchId = activeBranch?.id || null;
  const stockScopeLocationId = activeBranch?.defaultLocationId || null;

  // ── Fast-write caches (loaded once at POS open) ──
  const cachesRef = useRef(null);

  // ── POS receipt config (loaded once at mount; falls back to defaults if absent) ──
  const [posReceiptCfg, setPosReceiptCfg] = useState(null);

  // ── Payment Bar ──
  const [cashTendered, setCashTendered] = useState('');
  const [busySaving, setBusySaving] = useState(false);

  // ── Search ──
  const [searchInput, setSearchInput] = useState('');

  // ── Click-to-edit modal (Qty / Price / Discount on cart lines) ──
  // editTarget shape: { lineIndex, field: 'qty'|'price'|'discount', mode: 'amount'|'percent' }
  const [editTarget, setEditTarget] = useState(null);

  // ── Customer Dropdown ──
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  // ── Quick-add customer (POS) ──
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustName, setNewCustName] = useState('');
  const [newCustPhone, setNewCustPhone] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);

  // ── Delete bill (admin-password approved) ──
  const [deleteBillTarget, setDeleteBillTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const customerDropdownRef = useRef(null);

  // ── Modals ──
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // null | 'return' | 'exchange' — drives the receipt picker UI
  const [pickerMode, setPickerMode] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Parked sales (Hold/Recall) — persisted to localStorage per cashier/branch ──
  // Key is scoped so two cashiers / two branches never see each other's parked
  // sales. Survives browser refresh and power-cut between Hold and Recall.
  const parkedStorageKey = useMemo(() => {
    if (!companyId || !currentUser?.uid) return null;
    return `amal.pos.parked.${companyId}.${activeBranch?.id || 'none'}.${currentUser.uid}`;
  }, [companyId, activeBranch?.id, currentUser?.uid]);
  const [parkedSales, setParkedSales] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      // Optimistically read from any key matching this user — refined below
      // once parkedStorageKey resolves. Empty array is safe default.
      return [];
    } catch { return []; }
  });
  const [showParkedDropdown, setShowParkedDropdown] = useState(false);
  const parkedRef = useRef(null);

  // Load parked sales for the resolved storage key
  useEffect(() => {
    if (!parkedStorageKey) return;
    try {
      const raw = window.localStorage.getItem(parkedStorageKey);
      if (raw) setParkedSales(JSON.parse(raw) || []);
    } catch (e) { /* corrupted — start empty */ }
  }, [parkedStorageKey]);

  // Write whenever the list changes
  useEffect(() => {
    if (!parkedStorageKey) return;
    try { window.localStorage.setItem(parkedStorageKey, JSON.stringify(parkedSales)); }
    catch (e) { /* quota exceeded / private mode — non-fatal */ }
  }, [parkedSales, parkedStorageKey]);

  // ── Last sale (for Reprint / Repeat) ──
  const lastSaleRef = useRef(null);

  // ── Toasts (non-blocking notices) ──
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, tone = 'info') => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── POS-01: a printed sale whose background save needs the cashier's
  // attention. Shown one at a time; must be acknowledged (payment is blocked).
  const [saveAlerts, setSaveAlerts] = useState([]);
  // The save went to the offline outbox (same id) instead of the cloud.
  const reportQueuedSale = useCallback((label, docNumber, outcome) => {
    if (outcome.temporary) {
      showToast(`${label} ${docNumber}: no connection to the server — kept on this till, it will upload automatically.`, 'info');
      return;
    }
    const reason = outcome.error?.message || String(outcome.error || 'unknown error');
    setSaveAlerts(prev => [...prev, {
      title: `${label} ${docNumber} is pending on this till`,
      text: `The receipt was printed, but the server did not accept the save: ${reason}. ` +
        'It is kept on this till as a PENDING sale and will be retried automatically. Please inform your manager.'
    }]);
  }, [showToast]);
  // The save failed AND the sale could not be kept on this device.
  const reportUnsavedSale = useCallback((label, docNumber, err) => {
    setSaveAlerts(prev => [...prev, {
      title: `${label} ${docNumber} was NOT saved`,
      text: `The receipt was printed, but it could not be saved: ${err?.message || err}. ` +
        'Stock has been put back. Keep the receipt and enter it again.'
    }]);
  }, []);

  // ── Clock ──
  const [clockTime, setClockTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setClockTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // ── Close customer dropdown on outside click ──
  useEffect(() => {
    if (!showCustomerDropdown && !showParkedDropdown) return;
    const handleClick = (e) => {
      if (showCustomerDropdown && customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) {
        setShowCustomerDropdown(false); setCustomerSearch('');
      }
      if (showParkedDropdown && parkedRef.current && !parkedRef.current.contains(e.target)) {
        setShowParkedDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCustomerDropdown, showParkedDropdown]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.toLowerCase();
    return customers.filter(c => {
      const name = (c.companyName || c.customerName || '').toLowerCase();
      return c.isActive !== false && name.includes(q);
    });
  }, [customers, customerSearch]);

  const handleSelectCustomer = useCallback((cust) => {
    if (cust) {
      setCustomerId(cust.id);
      setCustomerName(cust.companyName || cust.customerName || 'Customer');
    } else if (walkInCustomer) {
      setCustomerId(walkInCustomer.customerId);
      setCustomerName(walkInCustomer.customerName);
    }
    setShowCustomerDropdown(false);
    setCustomerSearch('');
  }, [walkInCustomer]);

  // Phone is unique: if the typed number already exists, we reuse that customer
  // instead of creating a duplicate.
  const matchedCustomer = useMemo(() => {
    const digits = normalizePhone(newCustPhone);
    if (digits.length < 4) return null;
    return customers.find(c => normalizePhone(c.phone || c.whatsappNumber) === digits) || null;
  }, [customers, newCustPhone]);

  // Quick-add a customer from the POS (phone + name). Phone is the unique key —
  // if it already exists we select that customer (no duplicate). Otherwise create
  // the customer + its linked receivable account (so credit sales work), wire it
  // into the fast-write cache, then select it. Phone is stored for WhatsApp.
  const handleQuickAddCustomer = useCallback(async () => {
    if (!isOnline()) { showToast('Connect to the internet to add a customer', 'error'); return; }
    const phone = newCustPhone.trim();
    const digits = normalizePhone(phone);
    if (digits.length < 4) { showToast('Enter a valid phone number', 'error'); return; }
    // Dedupe guard: never add the same number twice.
    const existing = customers.find(c => normalizePhone(c.phone || c.whatsappNumber) === digits);
    if (existing) {
      handleSelectCustomer(existing);
      setShowAddCustomer(false);
      setNewCustName(''); setNewCustPhone('');
      showToast(`Selected existing customer "${existing.companyName || existing.customerName}"`, 'success');
      return;
    }
    const name = newCustName.trim();
    if (!name) { showToast('Enter customer name', 'error'); return; }
    setAddingCustomer(true);
    try {
      const customersRef = collection(db, 'companies', companyId, 'customers');
      const data = {
        companyName: name,
        phone: phone || '',
        whatsappNumber: phone || '',
        isActive: true,
        source: 'pos',
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.uid || null,
        updatedAt: new Date().toISOString()
      };
      await ensureParentAccountExists(companyId, 'customer', currentUser?.uid);
      const ref = await addDoc(customersRef, data);
      let linkedAccountId = null, linkedAccountCode = null;
      try {
        const res = await createLinkedAccount(companyId, 'customer', ref.id, name, currentUser?.uid);
        if (res?.success) {
          linkedAccountId = res.accountId;
          linkedAccountCode = res.accountCode;
          await updateDoc(doc(customersRef, ref.id), { linkedAccountId, linkedAccountCode });
        }
      } catch (accErr) {
        console.warn('[POS] quick-add linked account failed:', accErr);
      }
      const newCust = { id: ref.id, ...data, linkedAccountId, linkedAccountCode };
      setCustomers(prev => [...prev, newCust]);
      // Make the new customer usable for credit sales in this session.
      if (cachesRef.current && linkedAccountId) {
        cachesRef.current.partyAccountByCustomerId = cachesRef.current.partyAccountByCustomerId || {};
        cachesRef.current.partyAccountByCustomerId[ref.id] = { id: linkedAccountId, code: linkedAccountCode };
      }
      handleSelectCustomer(newCust);
      setShowAddCustomer(false);
      setNewCustName(''); setNewCustPhone('');
      showToast(`Customer "${name}" added`, 'success');
    } catch (e) {
      console.error('[POS] quick add customer failed:', e);
      showToast(`Could not add customer: ${e.message}`, 'error');
    } finally {
      setAddingCustomer(false);
    }
  }, [companyId, customers, newCustName, newCustPhone, currentUser, handleSelectCustomer, showToast]);

  const isWalkIn = customerId === walkInCustomer?.customerId;

  useEffect(() => {
    if (!companyId || !customerId) {
      setCustomerPricing(EMPTY_PRICING);
      return;
    }
    let cancelled = false;
    loadCustomerPricing(companyId, customerId, isModuleEnabled)
      .then(p => { if (!cancelled) setCustomerPricing(p); })
      .catch(() => { if (!cancelled) setCustomerPricing(EMPTY_PRICING); });
    return () => { cancelled = true; };
  }, [companyId, customerId, isModuleEnabled]);

  // Previous balance for the receipt — only when the toggle is ON and a named
  // (non-walk-in) customer is selected, so walk-in sales pay no query cost.
  useEffect(() => {
    if (!posReceiptCfg?.showPreviousBalance || !companyId || !customerId || isWalkIn) {
      setCustomerBalance(null);
      return;
    }
    let cancelled = false;
    const branchId = activeCounter?.branchId || activeBranch?.id || null;
    getCustomerBalance(companyId, customerId, branchId, activeFinancialYear?.id)
      .then(bal => { if (!cancelled) setCustomerBalance(bal); })
      .catch(() => { if (!cancelled) setCustomerBalance(null); });
    return () => { cancelled = true; };
  }, [posReceiptCfg, companyId, customerId, isWalkIn, activeCounter, activeBranch, activeFinancialYear]);

  // Stock Audit freeze — poll so a count started AFTER the POS was opened still
  // shows the banner and blocks selling. No-op when nothing is being counted.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const check = async () => {
      const locId = activeBranch?.defaultLocationId || cachesRef.current?.defaultLocation?.id || null;
      if (!locId) return;
      try {
        const locked = await isLocationLocked(companyId, locId);
        if (!cancelled) setAuditLocked(!!locked);
      } catch (_) { /* never break POS on this */ }
    };
    check();
    const t = setInterval(check, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [companyId, activeBranch, loading]);

  // ── Data Loading (parallelized + caches) ──
  useEffect(() => {
    if (!companyId) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const catRef = collection(db, 'companies', companyId, 'inventoryCategories');
        const fmtRef = doc(db, 'companies', companyId, 'settings', 'documentFormats');

        // Tier 1 (Firebase cost): items / accounts / customers now come from the
        // device-local cache (0 Firestore reads on a warm cache) instead of a full
        // getDocs on EVERY POS open — the single most frequent read on this screen.
        // Categories stay a direct read (tiny collection, not in the master cache).
        const [itemsAll, catSnap, accsAll, custAll, fastCaches, wi, fmtSnap] = await Promise.all([
          getItemsCached(companyId),
          getDocs(catRef),
          getAccountsCached(companyId),
          getCustomersCached(companyId),
          preloadPOSCaches(companyId),
          ensureWalkInCustomer(companyId, currentUser.uid),
          getDoc(fmtRef).catch(() => null)
        ]);

        // POS receipt config: nullable — POSReceipt.js falls back to defaults if missing.
        const posRcpt = (fmtSnap && fmtSnap.exists?.()) ? (fmtSnap.data()?.posReceipt || null) : null;
        setPosReceiptCfg(posRcpt);

        const itemsList = itemsAll
          .filter(i => i.isActive !== false && i.isDeleted !== true);
        setItems(itemsList);
        setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true));

        const accs = accsAll
          .filter(a => {
            const code = a.code || '';
            return (code.startsWith('11101') || code.startsWith('11102')) && a.level === 5;
          });
        setPaymentAccounts(accs);
        setCustomers(custAll);
        cachesRef.current = fastCaches;

        setWalkInCustomer(wi);
        setCustomerId(wi.customerId);
        setCustomerName(wi.customerName);

        // Stock balances loaded in the background — doesn't block first paint.
        // Filter to the branch's POS stock location (same one sales deduct from)
        // so a multi-location branch shows only the till's location, not the sum.
        // NOTE: branch/location here MUST match stockScopeBranchId /
        // stockScopeLocationId — they key the persisted copy of this map.
        const ids = itemsList.map(i => i.id);
        getStockBalancesCached(companyId, ids, activeBranch?.id || null, activeBranch?.defaultLocationId || null)
          .then(balances => setStockBalances(balances))
          .catch(err => console.error('Stock balance load failed:', err));
      } catch (error) {
        console.error('Error loading POS data:', error);
      } finally {
        setLoading(false);
      }

      // POS-Offline: kick off a master-data sync to Dexie in the background.
      // This keeps the local cache fresh so the user can drop offline at any
      // moment without losing access to customers/items/accounts. Non-blocking
      // — failure here just means offline mode will use the previous snapshot.
      if (isOnline() && companyId) {
        syncMasterDataToLocal(companyId).catch(err =>
          console.warn('[POSScreen] master sync to local failed:', err)
        );
      }
    };
    loadData();
  }, [companyId, currentUser, activeBranch]);

  // Category-first product grid: the grid has no "All" tab any more, so the
  // FIRST category is selected as soon as categories land. A company with NO
  // categories keeps this null and the grid falls back to showing every item
  // (see POSProductGrid) — never a blank screen. Search/scan is unaffected:
  // it always matches across the full item list regardless of this selection.
  useEffect(() => {
    if (selectedCategoryId) return;
    if (!categories || categories.length === 0) return;
    setSelectedCategoryId(categories[0].id);
  }, [categories, selectedCategoryId]);

  // POS-Offline: pre-allocate a per-counter doc-number range so the device is
  // ready for offline sales, and re-allocate on reconnect. Runs once the bound
  // counter is known (ranges are sharded per counter).
  useEffect(() => {
    if (!companyId || !activeCounter?.id) return;
    const allocate = () => {
      ensureRangeAllocated({ companyId, tranTypeCode: 'PS', counter: activeCounter, isOnline: true }).catch(() => {});
      ensureRangeAllocated({ companyId, tranTypeCode: 'SR', counter: activeCounter, isOnline: true }).catch(() => {});
    };
    if (isOnline()) allocate();
    const unsub = onConnectivityChange((online) => { if (online) allocate(); });
    return unsub;
  }, [companyId, activeCounter]);

  // ── Load the bound POS counter (from ?counter= in the URL) ──
  useEffect(() => {
    if (!companyId || !counterId) { setActiveCounter(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId, 'posCounters', counterId));
        if (cancelled) return;
        setActiveCounter(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) {
        if (!cancelled) setActiveCounter(null);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, counterId]);

  // ── Resolve the open shift for the bound counter ──
  // No open shift → prompt to open one before any selling is allowed.
  useEffect(() => {
    if (!companyId || !activeCounter?.id) { setActiveShift(null); setShiftChecked(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const shift = await getOpenShift(companyId, activeCounter.id);
        if (cancelled) return;
        setActiveShift(shift);
        setShiftChecked(true);
        cacheOpenShift(companyId, activeCounter.id, shift); // cache truth (clears when null)
        if (!shift) {
          // Opening float = current GL balance of the counter's cash account.
          let glBal = 0;
          try { glBal = await getCashAccountBalance(companyId, activeCounter.cashAccountId); } catch { /* default 0 */ }
          if (cancelled) return;
          setOpeningCashInput(String(glBal || 0));
          setShowOpenShift(true);
        }
      } catch (e) {
        // Offline (or read failed): fall back to the cached open shift so the
        // cashier can keep selling offline after a reload. If none cached, they
        // must reconnect to open a shift.
        if (cancelled) return;
        const cached = readCachedOpenShift(companyId, activeCounter.id);
        setActiveShift(cached || null);
        setShiftChecked(true);
        if (!cached) setShowOpenShift(true);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, activeCounter]);

  // ── Cart Calculations ──
  // Tax is INCLUSIVE: a taxable item's price already contains GST; the engine
  // back-computes net + GST per line. Non-taxable items pass through untaxed.
  const bankDiscountSpec = useMemo(
    () => (bankDiscount ? { percent: bankDiscount.percent, maxAmount: bankDiscount.maxAmount } : null),
    [bankDiscount]
  );
  // Return against a saved receipt: those lines refund what the customer paid
  // (their saved price is already WITHOUT tax) — see calculatePOSReturnLine.
  const totals = useMemo(
    () => calculatePOSTotals(cart, 'inclusive', headerDiscount, bankDiscountSpec, returnMode ? { receiptReturn: true } : null),
    [cart, headerDiscount, bankDiscountSpec, returnMode]
  );

  // Bill changed after a bank offer was applied → drop it (re-apply re-checks limits).
  useEffect(() => {
    if (bankDiscountRef.current) {
      bankDiscountRef.current = null;
      setBankDiscount(null);
      showToast('Bank discount removed — the bill changed. Apply the offer again.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, headerDiscount]);

  // Load the company's bank offers once (module ON only).
  useEffect(() => {
    if (!bankDiscountsEnabled || !companyId) { setBankOffers([]); return undefined; }
    let cancelled = false;
    loadBankDiscountOffers(companyId)
      .then(rows => { if (!cancelled) setBankOffers(rows); })
      .catch(err => console.warn('[POS] bank offers load failed:', err));
    return () => { cancelled = true; };
  }, [bankDiscountsEnabled, companyId]);

  // Exchange totals — sum of selected return lines at their original prices.
  // Each line's lineTotal honours per-line discount; we re-derive when the
  // cashier changes returnQty so the math always matches the original sale.
  const exchangeReturnTotal = useMemo(() => {
    if (!exchangeMode) return 0;
    return exchangeReturnLines.reduce((sum, l) => {
      if (!l._selected) return sum;
      const qty = Number(l.returnQty) || 0;
      const origQty = Number(l.quantity) || 1;
      const origTotal = Number(l.lineTotal) || (origQty * (Number(l.unitPrice) || 0));
      // Pro-rate so partial returns keep the original per-unit effective price.
      const perUnit = origQty > 0 ? origTotal / origQty : 0;
      return sum + qty * perUnit;
    }, 0);
  }, [exchangeMode, exchangeReturnLines]);

  // Net the customer owes (or is owed if negative) under exchange mode.
  const exchangeNet = exchangeMode ? (totals.totalAmount - exchangeReturnTotal) : 0;
  const exchangePayable = Math.max(0, exchangeNet);     // shown to PaymentBar as the amount to charge
  const exchangeRefundable = Math.max(0, -exchangeNet); // amount to refund the customer

  // ── Add Item to Cart ──
  const addToCart = useCallback((item, qty = 1) => {
    setCart(prev => {
      const existing = prev.findIndex(c => c.itemId === item.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], quantity: updated[existing].quantity + qty };
        setSelectedCartIndex(existing);
        return updated;
      }
      // Pick the price for the currently selected type (Retail/Wholesale/Dealer).
      // Falls back to the Retail sale price when the chosen type is blank/0.
      const pt = priceTypeRef.current;
      const priceOf = (src) => {
        const v = pt === 'Wholesale' ? Number(src.wholesalePrice)
          : pt === 'Dealer' ? Number(src.dealerPrice)
          : Number(src.salePrice);
        return v || Number(src.salePrice) || 0;
      };
      let seededUnit = item.unitOfMeasure || 'Pcs';
      let seededPrice = priceOf(item);
      let seededCost = Number(item.averageCost) || Number(item.purchasePrice) || 0;
      if (Array.isArray(item.units) && item.units.length > 0) {
        const baseRow = item.units.find(u => u.isBase) || item.units[0];
        const defaultRow = item.defaultSaleUnit
          ? (item.units.find(u => u.unitCode === item.defaultSaleUnit) || baseRow)
          : baseRow;
        if (defaultRow) {
          const conv = Number(defaultRow.conversion) || 1;
          seededUnit = defaultRow.unitCode;
          seededPrice = priceOf(defaultRow) || (priceOf(item) * conv);
          seededCost = Number(item.averageCost) ? Number(item.averageCost) * conv : (Number(defaultRow.purchasePrice) || (Number(item.purchasePrice) || 0) * conv); // INV-04
        }
      }
      const ovr = getOverridePrice(customerPricing, item.id);
      if (ovr !== null) seededPrice = ovr;
      const newLine = {
        itemId: item.id,
        itemCode: item.itemCode || '',
        itemName: item.itemName || '',
        barcode: item.barcode || '',
        description: item.description || '',
        quantity: qty,
        unitPrice: seededPrice,
        unitCost: seededCost,
        discountPercent: 0,
        discountAmount: 0,
        // Tax seeded from the item master (price is treated as GST-inclusive).
        taxable: item.taxable !== false,
        taxRate: Number(item.gstRate) || 0,
        unitOfMeasure: seededUnit,
        unitCode: seededUnit,
        // Price type used to seed this line (Retail/Wholesale/Dealer) — editable
        // per line when the pos_price_type feature is on.
        priceType: pt,
        // POS weight-scale plugin: marks a line whose qty is captured from the scale.
        soldByWeight: item.soldByWeight === true
      };
      setSelectedCartIndex(prev.length);
      return [...prev, newLine];
    });
  }, [customerPricing]);

  // ── Cart Operations ──
  const updateCartItem = useCallback((index, updates) => {
    setCart(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  }, []);

  // ── POS Weight Scale plugin (module: pos_weight_scale) ──
  // Capture target = the selected cart line if it's "sold by weight", else the
  // most recent weighable line. The widget's "Capture" writes the scale weight
  // into that line's quantity. No effect when the module is off.
  const weightScaleEnabled = !!(isModuleEnabled && isModuleEnabled('pos_weight_scale'));
  const activeWeighIndex = useMemo(() => {
    if (selectedCartIndex >= 0 && cart[selectedCartIndex]?.soldByWeight) return selectedCartIndex;
    for (let i = cart.length - 1; i >= 0; i--) { if (cart[i]?.soldByWeight) return i; }
    return -1;
  }, [cart, selectedCartIndex]);
  const weightCaptureLabel = activeWeighIndex >= 0 ? (cart[activeWeighIndex]?.itemName || 'item') : null;
  const handleWeightCapture = useCallback((kg) => {
    if (activeWeighIndex < 0) return;
    const q = Math.max(0.0001, Math.round((Number(kg) || 0) * 1000) / 1000);
    updateCartItem(activeWeighIndex, { quantity: q });
  }, [activeWeighIndex, updateCartItem]);

  const changeCartItemUnit = useCallback((index, unitCode) => {
    setCart(prev => {
      const updated = [...prev];
      const line = updated[index];
      if (!line) return prev;
      const item = items.find(i => i.id === line.itemId);
      if (!item || !Array.isArray(item.units)) return prev;
      const unitRow = item.units.find(u => u.unitCode === unitCode);
      if (!unitRow) return prev;
      const conv = Number(unitRow.conversion) || 1;
      let nextPrice = Number(unitRow.salePrice) || (Number(item.salePrice) || 0) * conv;
      const ovr = getOverridePrice(customerPricing, line.itemId);
      if (ovr !== null) nextPrice = ovr;
      // Show Two Units: switching between the item's two units keeps the same
      // physical qty (10 Ctn → 14.4 Mtr). Returns against a receipt keep their qty.
      const twoQ = line._ret ? null : twoUnitSwitchQty(item, line.unitCode || line.unitOfMeasure, unitCode, line.quantity);
      updated[index] = {
        ...line, unitCode, unitOfMeasure: unitCode,
        unitPrice: nextPrice,
        unitCost: Number(item.averageCost) ? Number(item.averageCost) * conv : (Number(unitRow.purchasePrice) || (Number(item.purchasePrice) || 0) * conv), // INV-04
        ...(twoQ !== null ? { quantity: twoQ } : {})
      };
      return updated;
    });
  }, [items, customerPricing]);

  // Change a single line's price type (Retail/Wholesale/Dealer) and re-price it
  // from the item master — same rule as addToCart. Blank type falls back to Retail.
  const changeCartItemPriceType = useCallback((index, newType) => {
    setCart(prev => {
      const updated = [...prev];
      const line = updated[index];
      if (!line) return prev;
      const item = items.find(i => i.id === line.itemId);
      if (!item) { updated[index] = { ...line, priceType: newType }; return updated; }
      const priceOf = (src) => {
        const v = newType === 'Wholesale' ? Number(src.wholesalePrice)
          : newType === 'Dealer' ? Number(src.dealerPrice)
          : Number(src.salePrice);
        return v || Number(src.salePrice) || 0;
      };
      let nextPrice = priceOf(item);
      if (Array.isArray(item.units) && item.units.length > 0) {
        const row = item.units.find(u => u.unitCode === (line.unitCode || line.unitOfMeasure));
        if (row) {
          const conv = Number(row.conversion) || 1;
          const rowByType = newType === 'Wholesale' ? Number(row.wholesalePrice)
            : newType === 'Dealer' ? Number(row.dealerPrice)
            : Number(row.salePrice);
          nextPrice = rowByType || (priceOf(item) * conv);
        }
      }
      const ovr = getOverridePrice(customerPricing, line.itemId);
      if (ovr !== null) nextPrice = ovr;
      updated[index] = { ...line, priceType: newType, unitPrice: nextPrice };
      return updated;
    });
  }, [items, customerPricing]);

  const removeCartItem = useCallback((index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
    setSelectedCartIndex(-1);
  }, []);

  const clearCart = useCallback((keepCustomer = false) => {
    bankDiscountRef.current = null;
    setBankDiscount(null);
    returnBankDiscRef.current = null;
    setCart([]);
    setSelectedCartIndex(-1);
    setHeaderDiscount(0);
    setMobileStep('products');   // mobile: return to product browsing after a sale/void/return
    setMobileMoreOpen(false);
    setReturnMode(false);
    setReturnOriginalId(null);
    setReturnOriginalDoc('');
    setExchangeMode(false);
    setExchangeOriginalId(null);
    setExchangeOriginalDoc('');
    setExchangeReturnLines([]);
    setExchangeReason('Customer Change');
    setEditTarget(null);
    setCashTendered('');
    if (!keepCustomer && walkInCustomer) {
      setCustomerId(walkInCustomer.customerId);
      setCustomerName(walkInCustomer.customerName);
    }
  }, [walkInCustomer]);

  // ── Smart Input (search box) ──
  const trySmartInputAction = useCallback(() => {
    const parsed = parsePOSInput(searchInput);
    if (parsed.type === 'empty') return false;

    if (parsed.type === 'codeAdd') {
      const item = items.find(i => (i.itemCode || '').toLowerCase() === parsed.code.toLowerCase());
      if (!item) { showToast(`No item with code ${parsed.code}`, 'error'); return true; }
      if (customerPricing.exclusive && customerPricing.allowedItemIds && !customerPricing.allowedItemIds.has(item.id)) {
        showToast(`"${item.itemName}" not in customer's price list`, 'error'); return true;
      }
      addToCart(item); setSearchInput(''); return true;
    }

    if (parsed.type === 'qtyAdd') {
      const item = findItemByTerm(items, parsed.term);
      if (!item) { showToast(`No item matching "${parsed.term}"`, 'error'); return true; }
      if (customerPricing.exclusive && customerPricing.allowedItemIds && !customerPricing.allowedItemIds.has(item.id)) {
        showToast(`"${item.itemName}" not in customer's price list`, 'error'); return true;
      }
      addToCart(item, parsed.qty); setSearchInput(''); return true;
    }

    if (parsed.type === 'price' || parsed.type === 'discAmt' || parsed.type === 'discPct') {
      if (selectedCartIndex < 0) { showToast('Select a cart line first', 'error'); return true; }
      if (parsed.type === 'price' && !canEditPrice) { showToast('You do not have permission to change the price', 'error'); setSearchInput(''); return true; }
      if (parsed.type === 'price') updateCartItem(selectedCartIndex, { unitPrice: parsed.value });
      else if (parsed.type === 'discAmt') updateCartItem(selectedCartIndex, { discountAmount: parsed.value, discountPercent: 0 });
      else updateCartItem(selectedCartIndex, { discountPercent: parsed.value, discountAmount: 0 });
      setSearchInput(''); return true;
    }

    if (parsed.type === 'search') {
      const item = findItemByTerm(items, parsed.term);
      if (item) {
        if (customerPricing.exclusive && customerPricing.allowedItemIds && !customerPricing.allowedItemIds.has(item.id)) {
          showToast(`"${item.itemName}" not in customer's price list`, 'error'); return true;
        }
        addToCart(item); setSearchInput(''); return true;
      }
      showToast(`No item matches "${parsed.term}"`, 'error'); return true;
    }
    return false;
  }, [searchInput, items, customerPricing, selectedCartIndex, addToCart, updateCartItem, showToast]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || !searchInput.trim()) return;
    e.preventDefault();
    trySmartInputAction();
  }, [searchInput, trySmartInputAction]);

  // ── Barcode Scanner ──
  const handleBarcodeScan = useCallback((barcode) => {
    // Returns a result so the camera scanner (a full-screen overlay that hides
    // the cart + toasts) can confirm the hit on-screen. Hardware scanner ignores
    // the return value.
    const item = items.find(i => i.barcode && i.barcode === barcode);
    let result;
    if (item) {
      if (customerPricing.exclusive && customerPricing.allowedItemIds && !customerPricing.allowedItemIds.has(item.id)) {
        showToast(`"${item.itemName}" not in price list for this customer`, 'error');
        result = { ok: false, message: `Not allowed: ${item.itemName}` };
      } else {
        addToCart(item);
        result = { ok: true, message: `Added: ${item.itemName}` };
      }
    } else {
      showToast(`Item not found for barcode: ${barcode}`, 'error');
      result = { ok: false, message: `No item for ${barcode}` };
    }
    setSearchInput('');
    return result;
  }, [items, addToCart, customerPricing, showToast]);

  useBarcodeScanner(handleBarcodeScan, { enabled: !loading });

  // ── Click-to-edit handlers (qty, price, discount on each cart line) ──
  const openLineEditor = useCallback((lineIndex, field) => {
    if (field === 'price' && !canEditPrice) {
      showToast('You do not have permission to change the price', 'error');
      return;
    }
    setSelectedCartIndex(lineIndex);
    const line = cart[lineIndex];
    if (!line) return;
    const mode = field === 'discount'
      ? ((line.discountPercent || 0) > 0 ? 'percent' : 'amount')
      : 'amount';
    setEditTarget({ lineIndex, field, mode });
  }, [cart, canEditPrice, showToast]);

  const handleEditConfirm = useCallback((value, mode) => {
    if (!editTarget) return;
    const { lineIndex, field } = editTarget;
    if (field === 'qty') {
      updateCartItem(lineIndex, { quantity: Math.max(0.0001, value) });
    } else if (field === 'qty2') {
      // Show Two Units: qty typed in the item's second unit → line qty.
      const line = cart[lineIndex];
      const item = line && itemsById[line.itemId];
      const lineUnit = line ? (line.unitCode || line.unitOfMeasure) : '';
      const second = secondUnitForLine(item, lineUnit);
      if (second) updateCartItem(lineIndex, { quantity: Math.max(0.0001, qtyFromSecondQty(item, value, lineUnit, second)) });
    } else if (field === 'price') {
      updateCartItem(lineIndex, { unitPrice: Math.max(0, value) });
    } else if (field === 'discount') {
      if (mode === 'percent') {
        updateCartItem(lineIndex, { discountPercent: Math.min(100, Math.max(0, value)), discountAmount: 0 });
      } else {
        updateCartItem(lineIndex, { discountAmount: Math.max(0, value), discountPercent: 0 });
      }
    }
    setEditTarget(null);
  }, [editTarget, updateCartItem, cart, itemsById]);

  // ── Local stock decrement (avoid full refetch after every sale) ──
  const decrementStockLocally = useCallback((lines, sign = -1) => {
    // BASE units. The badge map is aggregated from stockFlow.quantity, which is
    // stored in BASE units, and the POS save writes base quantities too — so a
    // line sold in a bigger unit (1 Ctn = 24 Pcs) must be converted here, else
    // the badge (and the checkout check that now reads it) would drift by the
    // conversion factor. Single-unit items convert by 1 → unchanged behaviour.
    const deltas = {};
    (lines || []).forEach(l => {
      if (!l?.itemId) return;
      const item = itemsById[l.itemId];
      if ((item?.itemType || l.itemType) === 'service') return; // services hold no stock
      const { requestedBase } = toBaseQty(item || {}, l.quantity, l.unitCode || l.unitOfMeasure);
      if (!requestedBase) return;
      deltas[l.itemId] = (deltas[l.itemId] || 0) + sign * requestedBase;
    });
    if (Object.keys(deltas).length === 0) return;

    setStockBalances(prev => {
      const next = { ...prev };
      Object.entries(deltas).forEach(([itemId, delta]) => {
        // Only touch items the map actually knows about. An item absent from a
        // restored map must STAY unknown (no badge, live per-item check at
        // checkout) instead of being invented as a negative number.
        if (next[itemId] === undefined) return;
        next[itemId] = (Number(next[itemId]) || 0) + delta;
      });
      return next;
    });

    // Keep the PERSISTED copy (localStorage, survives reload / new tab) in step
    // with exactly the same delta — otherwise a reload would resurrect the
    // pre-sale numbers and the till would show stock it has already sold.
    applyPersistedStockDelta(companyId, stockScopeBranchId, stockScopeLocationId, deltas);

    // Tier 4 (Firebase cost): the cached POS stock badge (posbal) must not
    // survive our own sale — clear it so the NEXT fresh POS mount re-reads this
    // device's post-sale stock instead of serving a pre-sale snapshot.
    //
    // NARROW on purpose: the prefix is `…|posbal|`, not the whole
    // `stockSummary|{companyId}` namespace. The broad prefix ALSO matched the
    // transaction forms' badge cache (`stockSummary|{companyId}|{fy}|{criteria}`),
    // so one POS sale wiped every form's badge cache and the next form open had
    // to re-pay a full stockFlow scan. Those shared entries are kept accurate by
    // folding the sale's committed rows in — see foldSaleIntoStockCache below.
    if (companyId) invalidateCache(`stockSummary|${companyId}|posbal|`);
  }, [companyId, itemsById, stockScopeBranchId, stockScopeLocationId]);

  // Fold a COMMITTED POS sale's stockFlow rows into the shared stock-badge cache
  // (exact numbers, zero Firestore reads) instead of wiping it. Runs only after
  // the background save resolves, because the optimistic cart lines are NOT
  // stockFlow rows (no direction / location / base-unit conversion) — the real
  // rows come back on `_bundle.stockFlow`. Row direction already encodes sale vs
  // return, so the sign is always +1 ("these rows now exist").
  //
  // If the rows are unavailable (offline save path returns no bundle) or anything
  // unexpected happens, fall back to the old blanket invalidate: costlier, but a
  // badge can then never read stale-too-high after a sale on this device.
  const foldSaleIntoStockCache = useCallback((saveResult) => {
    try {
      const rows = saveResult?._bundle?.stockFlow;
      if (Array.isArray(rows)) {
        if (rows.length > 0) applyEntriesToStockCache(companyId, rows, +1);
        return;
      }
      if (companyId) invalidateCache(`stockSummary|${companyId}`);
    } catch (_) {
      if (companyId) invalidateCache(`stockSummary|${companyId}`);
    }
  }, [companyId]);

  // Route every cash-method payment leg to THIS counter's own cash account so
  // each drawer reconciles independently. Card/bank/credit legs are untouched.
  const routeCashToCounter = useCallback((payments) => {
    if (!activeCounter?.cashAccountId) return payments;
    return (payments || []).map(p =>
      (p.method || 'cash').toLowerCase() === 'cash'
        ? {
            ...p,
            accountId: activeCounter.cashAccountId,
            accountCode: activeCounter.cashAccountCode || p.accountCode || '',
            accountName: activeCounter.cashAccountName || p.accountName || 'Cash in Hand'
          }
        : p
    );
  }, [activeCounter]);

  // ── Exchange Pay: two batched saves (SR + PS), one combined receipt ──
  const handleExchangePay = useCallback(async (paymentResult) => {
    if (!canEdit) { showToast('No permission to save sales', 'error'); return; }
    if (posUserInactive) { showToast('Your user is inactive — ask the admin to activate it before selling.', 'error'); return; }
    if (!companyId || !activeFinancialYear || !cachesRef.current) return;

    const selectedReturn = exchangeReturnLines
      .filter(l => l._selected && Number(l.returnQty) > 0)
      .map(l => {
        const origQty = Number(l.quantity) || 1;
        const origTotal = Number(l.lineTotal) || (origQty * (Number(l.unitPrice) || 0));
        const perUnit = origQty > 0 ? origTotal / origQty : 0;
        const qty = Number(l.returnQty) || 0;
        const lineTotal = Math.round(qty * perUnit * 100) / 100; // tax-inclusive
        // Split out the GST portion so the return reverses the same tax (inclusive).
        const taxable = l.taxable !== false;
        const rate = Number(l.taxRate) || 0;
        const net = taxable && rate > 0 ? Math.round((lineTotal / (1 + rate / 100)) * 100) / 100 : lineTotal;
        const taxAmount = Math.round((lineTotal - net) * 100) / 100;
        return {
          itemId: l.itemId,
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description || '',
          quantity: qty,
          unitPrice: l.unitPrice || 0,
          unitCost: l.unitCost || 0,
          discountPercent: l.discountPercent || 0,
          discountAmount: 0,
          taxable,
          taxRate: rate,
          taxAmount,
          netAmount: net,
          grossAmount: net,
          priceExclTax: rate > 0 ? Math.round((Number(l.unitPrice) || 0) / (1 + rate / 100) * 100) / 100 : (Number(l.unitPrice) || 0),
          lineTotal,
          totalCost: qty * (Number(l.unitCost) || 0),
          unitOfMeasure: l.unitOfMeasure || 'Pcs',
          unitCode: l.unitCode || l.unitOfMeasure || ''
        };
      });

    if (selectedReturn.length === 0 && cart.length === 0) {
      showToast('Exchange must have at least a return or a new item', 'error');
      return;
    }

    // Hard-block negative stock on the new-items half of the exchange.
    if (cart.length > 0 && isModuleEnabled('inventory_block_negative')) {
      const stockItems = cart
        .filter(l => l.itemId && (Number(l.quantity) || 0) > 0)
        .map(l => ({
          itemId: l.itemId,
          itemName: l.itemName || l.description,
          quantity: l.quantity,
          unitCode: l.unitCode || l.unitOfMeasure,
        }));
      if (stockItems.length > 0) {
        // Firebase cost: reuse the grid's on-screen balances (BASE units, keyed
        // by itemId) + the already-loaded item records instead of re-reading
        // every cart item's stock history and item doc at checkout. Reuse is
        // decided per item inside checkStockAvailability, so an item missing
        // from the map still gets its own live read — never a false "0 stock".
        const insufficient = await checkStockAvailability(companyId, stockItems, {
          stockMap: stockBalances,
          itemsMap: itemsById
        });
        if (insufficient.length > 0) {
          const msg = insufficient
            .map(i => `${i.itemName}: available ${i.available} ${i.unit || ''}, in cart ${i.requested} ${i.unit || ''}`)
            .join('\n');
          showToast(`Cannot exchange — new items would drive stock below zero:\n${msg}`, 'error');
          return;
        }
      }
    }

    setBusySaving(true);
    const docDate = localDateStr(); // POS-06: Pakistan date, not UTC
    const cashier = userProfile?.displayName || userProfile?.fullName || 'Cashier';
    const finalCustomerId = paymentResult.customerId || customerId;
    const finalCustomerName = paymentResult.customerName || customerName;

    // Snapshot for receipt
    // Show Two Units: second-unit qty worked out fresh for the receipt (dropped when not applicable).
    const newSnap = totals.lines.map(({ secondUnitCode, secondQuantity, ...l }) => ({ ...l, ...(twoUnitLineFields(itemsById[l.itemId], l) || {}) }));
    const retSnap = selectedReturn.map(l => ({ ...l }));
    const returnSubtotal = retSnap.reduce((s, l) => s + l.lineTotal, 0);
    const srNeeded = retSnap.length > 0;
    const psNeeded = newSnap.length > 0;

    // Both halves are saved separately (SR + PS).
    //
    // Accounting model for an exchange (each transaction must balance on its own):
    //   SR side:  Dr SalesReturn (returnSubtotal)
    //             Dr Inventory (returnCOGS)
    //             Cr <payment-account> (returnSubtotal)   ← refund routed to same account
    //             Cr COGS (returnCOGS)
    //   PS side:  Dr <payment-account> (newTotal)         ← full new-items value charged
    //             Dr COGS (newCOGS)
    //             Cr Sales (newTotal)
    //             Cr Inventory (newCOGS)
    //
    // Net cash/bank movement at the till = newTotal − returnSubtotal = exchangeNet,
    // which is exactly what the cashier actually exchanged with the customer.
    //
    // For credit-method exchanges, the payment account is the customer A/R
    // (handled inside savePOSTransactionFast via partyAccountId), so the
    // customer's balance moves by the net naturally.
    const primaryPmt = (paymentResult.payments && paymentResult.payments[0]) || {
      method: 'cash', accountId: '', accountCode: '', accountName: 'Cash in Hand', reference: ''
    };
    const buildPmt = (amount) => ([{
      method: primaryPmt.method,
      accountId: primaryPmt.accountId || '',
      accountCode: primaryPmt.accountCode || '',
      accountName: primaryPmt.accountName || '',
      amount,
      reference: primaryPmt.reference || ''
    }]);

    let srSaleData = null;
    if (srNeeded) {
      const returnNet = Math.round(retSnap.reduce((s, l) => s + (Number(l.netAmount) || l.lineTotal), 0) * 100) / 100;
      const returnTax = Math.round(retSnap.reduce((s, l) => s + (Number(l.taxAmount) || 0), 0) * 100) / 100;
      const srTotals = {
        lines: retSnap,
        subtotal: returnNet, totalDiscount: 0, taxAmount: returnTax, totalAmount: returnSubtotal
      };
      srSaleData = {
        cart: retSnap,
        totals: srTotals,
        // Route the refund through the same account the customer used for
        // the PS half so both legs sum to the real net cash movement.
        payments: routeCashToCounter(buildPmt(returnSubtotal)),
        paymentMethod: primaryPmt.method,
        customerId: finalCustomerId,
        customerName: finalCustomerName,
        financialYearId: activeFinancialYear.id,
        cashierName: cashier,
        returnMode: true,
        returnOriginalId: exchangeOriginalId,
        branchId: activeCounter?.branchId || activeBranch?.id || null,
        branchName: activeCounter?.branchName || activeBranch?.branchName || null,
        // Deduct stock from THIS branch's assigned location (Branch Setup).
        locationId: activeBranch?.defaultLocationId || null,
        locationName: activeBranch?.defaultLocationName || null,
        counterId: activeCounter?.id || null,
        counterCode: activeCounter?.counterCode || null,
        shiftId: activeShift?.id || null,
        itemsCatalog: items
      };
    }
    // Override the payment amount to the FULL new-items total — not the
    // net the cashier tapped. The till math still nets to exchangeNet
    // once paired with the SR's Cr above.
    const psSaleData = psNeeded ? {
      cart: newSnap,
      totals,
      payments: routeCashToCounter(buildPmt(totals.totalAmount)),
      paymentMethod: primaryPmt.method,
      customerId: finalCustomerId,
      customerName: finalCustomerName,
      financialYearId: activeFinancialYear.id,
      cashierName: cashier,
      returnMode: false,
      returnOriginalId: null,
      branchId: activeCounter?.branchId || activeBranch?.id || null,
      branchName: activeCounter?.branchName || activeBranch?.branchName || null,
      // Deduct stock from THIS branch's assigned location (Branch Setup).
      locationId: activeBranch?.defaultLocationId || null,
      locationName: activeBranch?.defaultLocationName || null,
      counterId: activeCounter?.id || null,
      counterCode: activeCounter?.counterCode || null,
      shiftId: activeShift?.id || null,
      itemsCatalog: items
    } : null;

    // POS-01: check both halves (entries balance, accounts present) BEFORE any
    // number is taken or a receipt printed — pure work on the loaded caches.
    try {
      if (srSaleData) preparePOSSale(companyId, srSaleData, currentUser.uid, cachesRef.current, { validateOnly: true });
      if (psSaleData) preparePOSSale(companyId, psSaleData, currentUser.uid, cachesRef.current, { validateOnly: true });
    } catch (e) {
      console.error('[POS] Exchange check failed:', e);
      showToast(`Cannot complete this exchange: ${e.message}`, 'error');
      setBusySaving(false);
      return;
    }

    try {
      // Allocate both numbers up-front, in parallel, so we can print the
      // combined receipt before either batch commit completes.
      const [srDocNumber, psDocNumber] = await Promise.all([
        srNeeded ? allocatePOSDocNumber(companyId, 'SR', docDate, activeCounter?.branchId || activeBranch?.id || null, activeCounter) : Promise.resolve(null),
        psNeeded ? allocatePOSDocNumber(companyId, 'PS', docDate, activeCounter?.branchId || activeBranch?.id || null, activeCounter) : Promise.resolve(null)
      ]);
      // The complete saves (with their ids), built before printing.
      const srData = srSaleData ? { ...srSaleData, preAllocatedDocNumber: srDocNumber } : null;
      const psData = psSaleData ? { ...psSaleData, preAllocatedDocNumber: psDocNumber } : null;
      const srPrepared = srData ? preparePOSSale(companyId, srData, currentUser.uid, cachesRef.current) : null;
      const psPrepared = psData ? preparePOSSale(companyId, psData, currentUser.uid, cachesRef.current) : null;

      // Combined receipt: prints return lines (-), new lines (+), and net at the bottom.
      printReceipt({
        docNumber: psDocNumber || srDocNumber || 'EXCHANGE',
        docDate,
        cart: newSnap,
        subtotal: totals.subtotal,
        discountAmount: totals.totalDiscount,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
        paymentMethod: paymentResult.paymentMethod,
        payments: paymentResult.payments,
        cashTendered: paymentResult.cashTendered || 0,
        changeGiven: paymentResult.changeGiven || 0,
        customerName: finalCustomerName,
        cashierName: cashier,
        exchange: {
          isExchange: true,
          originalDocNumber: exchangeOriginalDoc,
          returnDocNumber: srDocNumber,
          returnLines: retSnap,
          returnSubtotal,
          reason: exchangeReason,
          netAmount: exchangeNet
        }
      }, activeCompany, posReceiptCfg);

      // Local stock adjustment: returned items go back IN, new items go OUT.
      if (retSnap.length > 0) decrementStockLocally(retSnap, +1);
      if (newSnap.length > 0) decrementStockLocally(newSnap, -1);

      lastSaleRef.current = null; // exchange isn't simply repeatable
      clearCart();
      setBusySaving(false);
      showToast(
        exchangeNet > 0.01 ? `Exchange done — customer charged ${formatCurrency(exchangeNet)}`
          : exchangeNet < -0.01 ? `Exchange done — refund ${formatCurrency(-exchangeNet)}`
          : 'Even exchange completed',
        'success'
      );

      // Background: commit SR + PS independently. A failed save is kept in the
      // offline outbox (same id) — never dropped; only if even that fails is the
      // local stock change reverted.
      const tasks = [];
      if (srPrepared) {
        tasks.push(
          savePreparedPOSSale(companyId, srData, currentUser.uid, cachesRef.current, srPrepared)
            .then((out) => {
              try {
                if (out.status === 'saved') { foldSaleIntoStockCache(out.result); return; }
                foldSaleIntoStockCache(null);
                reportQueuedSale('Return', srDocNumber, out);
              } catch (e) { console.warn('[POS] Exchange SR post-save step failed:', e); }
            }, (err) => {
              console.error('[POS] Exchange SR save failed:', err);
              decrementStockLocally(retSnap, -1); // revert
              reportUnsavedSale('Return', srDocNumber, err);
            })
        );
      }
      if (psPrepared) {
        tasks.push(
          savePreparedPOSSale(companyId, psData, currentUser.uid, cachesRef.current, psPrepared)
            .then((out) => {
              try {
                if (out.status === 'saved') { foldSaleIntoStockCache(out.result); return; }
                foldSaleIntoStockCache(null);
                reportQueuedSale('Sale', psDocNumber, out);
              } catch (e) { console.warn('[POS] Exchange PS post-save step failed:', e); }
            }, (err) => {
              console.error('[POS] Exchange PS save failed:', err);
              decrementStockLocally(newSnap, +1); // revert
              reportUnsavedSale('Sale', psDocNumber, err);
            })
        );
      }
      Promise.all(tasks); // fire-and-forget
    } catch (error) {
      console.error('[POS] Exchange allocation failed:', error);
      showToast(`Exchange failed: ${error.message}`, 'error');
      setBusySaving(false);
    }
  }, [canEdit, companyId, activeFinancialYear, currentUser, exchangeReturnLines, cart, totals,
      customerId, customerName, exchangeOriginalId, exchangeOriginalDoc, exchangeReason, exchangeNet,
      activeBranch, activeCompany, posReceiptCfg, userProfile, items, itemsById, stockBalances,
      decrementStockLocally,
      foldSaleIntoStockCache, clearCart, formatCurrency, showToast, isModuleEnabled,
      activeCounter, activeShift, routeCashToCounter, posUserInactive, reportQueuedSale, reportUnsavedSale]);

  // ── Shift handlers ──
  // Prefill the opening float from the counter cash account's live GL balance.
  const handleShowOpenShift = useCallback(async () => {
    let glBal = 0;
    try { glBal = await getCashAccountBalance(companyId, activeCounter?.cashAccountId); } catch { /* default 0 */ }
    setOpeningCashInput(String(glBal || 0));
    setShowOpenShift(true);
  }, [companyId, activeCounter]);

  const handleOpenShift = useCallback(async () => {
    if (!companyId || !activeCounter?.id) return;
    if (!isOnline()) { showToast('Connect to the internet to open a shift', 'error'); return; }
    setShiftBusy(true);
    try {
      const shift = await openShiftDoc(companyId, activeCounter, { ...currentUser, ...userProfile }, Number(openingCashInput) || 0);
      setActiveShift(shift);
      cacheOpenShift(companyId, activeCounter.id, shift);
      setShowOpenShift(false);
      showToast(`Shift ${shift.shiftNumber} opened`, 'success');
    } catch (e) {
      console.error('[POS] open shift failed:', e);
      showToast(`Could not open shift: ${e.message}`, 'error');
    } finally {
      setShiftBusy(false);
    }
  }, [companyId, activeCounter, currentUser, userProfile, openingCashInput, showToast]);

  const handleOpenCloseModal = useCallback(async () => {
    if (!activeShift) return;
    if (!isOnline()) { showToast('Connect to the internet to close the shift', 'error'); return; }
    setShiftBusy(true);
    try {
      // Guard: this shift's offline sales must be synced first, else the close
      // totals/variance would be understated. Try to drain, then re-check.
      let pending = await countPendingForShift(activeShift.id);
      if (pending > 0) {
        showToast(`Syncing ${pending} offline sale(s) before close…`, 'info');
        try { await drainOutbox({ force: true }); } catch { /* re-checked below */ }
        pending = await countPendingForShift(activeShift.id);
      }
      if (pending > 0) {
        // POS-01: sales the server keeps refusing (not a connection problem)
        // would block the close for ever — the owner / an admin may take them
        // off this till after reading why they were refused.
        const rows = await listPendingForShift(activeShift.id);
        const refused = rows.filter(r => r.status === 'failed' && r.lastError);
        const isManager = (activeCompany?.ownerId && currentUser?.uid === activeCompany.ownerId)
          || companyUser?.role === 'admin';
        if (refused.length === pending && isManager) {
          const nums = refused.map(r => r?.bundle?.transaction?.docNumber || '?').join(', ');
          const ok = window.confirm(
            `${refused.length} sale(s) on this till were refused by the server and cannot be saved as they are:\n` +
            `${nums}\n\nReason: ${refused[0].lastError}\n\n` +
            'Remove them from this till so the shift can close? They will NOT be in the books — ' +
            'enter them again by hand if the sale really happened.');
          if (ok) {
            await discardOutboxRows(refused.map(r => r.seq), currentUser?.uid);
            pending = await countPendingForShift(activeShift.id);
          }
        }
      }
      if (pending > 0) {
        showToast(`${pending} sale(s) for this shift haven't synced yet — sync them, then close.`, 'error');
        setShiftBusy(false);
        return;
      }
      const totals = await getShiftTotals(companyId, activeShift.id);
      const expectedCash = Math.round(((Number(activeShift.openingCash) || 0) + totals.cashSales - totals.cashReturns) * 100) / 100;
      setCloseTotals({ ...totals, expectedCash });
      setCountedCashInput(String(expectedCash));
      setCloseNotes('');
      setShowCloseShift(true);
    } catch (e) {
      console.error('[POS] load shift totals failed:', e);
      showToast(`Could not load shift totals: ${e.message}`, 'error');
    } finally {
      setShiftBusy(false);
    }
  }, [companyId, activeShift, showToast, activeCompany, currentUser, companyUser]);

  const handleConfirmCloseShift = useCallback(async () => {
    if (!activeShift) return;
    setShiftBusy(true);
    try {
      const closed = await closeShiftDoc(
        companyId, activeShift,
        { countedCash: Number(countedCashInput) || 0, notes: closeNotes },
        { ...currentUser, ...userProfile }
      );
      printZReport(closed, activeCompany, posReceiptCfg);
      cacheOpenShift(companyId, activeCounter?.id, null); // shift no longer open
      setShowCloseShift(false);
      setActiveShift(null);
      setCloseTotals(null);
      showToast(`Shift ${closed.shiftNumber} closed`, 'success');
      // Force re-prompt to open a new shift for continued selling. Opening float
      // comes from the counter cash GL balance (which now includes today's cash).
      let glBal = 0;
      try { glBal = await getCashAccountBalance(companyId, activeCounter?.cashAccountId); } catch { /* default 0 */ }
      setOpeningCashInput(String(glBal || 0));
      setShowOpenShift(true);
    } catch (e) {
      console.error('[POS] close shift failed:', e);
      showToast(`Could not close shift: ${e.message}`, 'error');
    } finally {
      setShiftBusy(false);
    }
  }, [companyId, activeShift, activeCounter, countedCashInput, closeNotes, currentUser, userProfile, activeCompany, showToast]);

  // ── Payment + Optimistic Save ──
  // ── Bank card offer: apply at Card payment ──
  const posBranchId = activeCounter?.branchId || activeBranch?.id || null;
  const validBankOffers = useMemo(
    () => (bankDiscountsEnabled ? offersValidNow(bankOffers, { branchId: posBranchId }) : []),
    // cart.length: re-check validity (date / time window) as sales go by
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bankDiscountsEnabled, bankOffers, posBranchId, cart.length]
  );

  const handleApplyBankDiscount = useCallback(async (offer, first6, last4) => {
    if (!bankDiscountsEnabled) return { ok: false, error: 'Bank discounts are not enabled for this company.' };
    if (returnMode || exchangeMode) return { ok: false, error: 'Bank discount is only for new sales.' };
    if (cart.length === 0) return { ok: false, error: 'Add items first.' };
    if (!isOnline()) return { ok: false, error: 'Bank discount needs internet (card limits are checked online).' };
    const reason = offerUnavailableReason(offer, { branchId: posBranchId });
    if (reason) return { ok: false, error: `${offerLabel(offer)}: ${reason}` };
    if (!cardMatchesOffer(offer, first6)) {
      return { ok: false, error: `This card is not a ${offerLabel(offer)} card (first 6 digits don't match the offer).` };
    }
    // The offer's claim / recovery accounts must exist (created when it was saved).
    try {
      const accts = (await getAccountsCached(companyId)) || [];
      const ok = (id) => !!id && accts.some(a => a.id === id && a.isActive !== false);
      if (!ok(offer.claimAccountId) || !ok(offer.recoveryAccountId)) {
        return { ok: false, error: 'Offer setup is incomplete — open Maintain → Bank Discounts and save this offer again.' };
      }
    } catch (e) { /* cache unavailable — the save's balance check still protects the books */ }
    const minBill = Number(offer.minBill) || 0;
    if (minBill > 0 && (totals.totalAmount || 0) < minBill) {
      return { ok: false, error: `Minimum bill for this offer is ${formatCurrency(minBill)}.` };
    }
    let usage;
    try {
      usage = await readCardUsage(companyId, offer.id, first6, last4);
    } catch (e) {
      return { ok: false, error: `Could not check this card's limits: ${e.message || e}` };
    }
    const lim = checkCardLimits(offer, usage);
    if (!lim.ok) return lim;
    const applied = {
      offerId: offer.id,
      label: offerLabel(offer),
      bankName: offer.bankName || '',
      cardType: offer.cardType || '',
      percent: Number(offer.discountPercent) || 0,
      maxAmount: maxDiscountForBill(offer, lim.remainingMonthAmount),
      shopSharePct: offer.shopSharePct,
      cardFirst6: first6,
      cardLast4: last4,
      cardMask: maskCard(first6, last4),
      claimAccountId: offer.claimAccountId, claimAccountCode: offer.claimAccountCode || '', claimAccountName: offer.claimAccountName || '',
      recoveryAccountId: offer.recoveryAccountId, recoveryAccountCode: offer.recoveryAccountCode || '', recoveryAccountName: offer.recoveryAccountName || '',
      usage
    };
    bankDiscountRef.current = applied;
    setBankDiscount(applied);
    showToast(`${applied.label} ${applied.percent}% applied`, 'success');
    return { ok: true };
  }, [bankDiscountsEnabled, returnMode, exchangeMode, cart.length, posBranchId, companyId, totals.totalAmount, formatCurrency, showToast]);

  const handleClearBankDiscount = useCallback(() => {
    bankDiscountRef.current = null;
    setBankDiscount(null);
  }, []);

  const handlePaymentComplete = useCallback(async (paymentResult) => {
    if (!activeCounter) { showToast('Open the POS from a counter in the Sales menu', 'error'); return; }
    if (!activeShift) { showToast('Open a shift before selling', 'error'); setShowOpenShift(true); return; }

    // Stock Audit freeze — MUST be checked here, before the optimistic path below
    // allocates a doc number, prints the receipt and clears the cart. The background
    // save is rejected too, but by then the cashier has already seen a receipt.
    if (companyId) {
      const auditLocId = activeBranch?.defaultLocationId || cachesRef.current?.defaultLocation?.id || null;
      if (auditLocId) {
        try {
          await assertPosLocationUnlocked(companyId, auditLocId);
        } catch (e) {
          showToast(e.message, 'error');
          return;
        }
      }
    }

    if (exchangeMode) return handleExchangePay(paymentResult);
    if (!canEdit) { showToast('You do not have permission to save sales', 'error'); return; }
    if (posUserInactive) { showToast('Your user is inactive — ask the admin to activate it before selling.', 'error'); return; }
    if (!companyId || !activeFinancialYear) return;
    if (!cachesRef.current) { showToast('Caches not ready, please wait', 'error'); return; }
    if (cart.length === 0) return;

    // Hard-block negative stock (forward sales only — returns add stock back).
    if (!returnMode && isModuleEnabled('inventory_block_negative')) {
      const stockItems = cart
        .filter(l => l.itemId && (Number(l.quantity) || 0) > 0)
        .map(l => ({
          itemId: l.itemId,
          itemName: l.itemName || l.description,
          quantity: l.quantity,
          unitCode: l.unitCode || l.unitOfMeasure,
        }));
      if (stockItems.length > 0) {
        // Firebase cost: same reuse as the exchange path above — the till already
        // holds these numbers (they are what the tiles show, decremented after
        // every sale on this device), so a checkout costs 0 extra reads.
        const insufficient = await checkStockAvailability(companyId, stockItems, {
          stockMap: stockBalances,
          itemsMap: itemsById
        });
        if (insufficient.length > 0) {
          const msg = insufficient
            .map(i => `${i.itemName}: available ${i.available} ${i.unit || ''}, in cart ${i.requested} ${i.unit || ''}`)
            .join('\n');
          showToast(`Cannot sell — would drive stock below zero:\n${msg}`, 'error');
          return;
        }
      }
    }

    // Bank card discount guards (module pos_bank_discounts).
    const bdApplied = !returnMode && !exchangeMode && bankDiscount && (totals.bankDiscountAmount || 0) > 0 ? bankDiscount : null;
    if (bankDiscount && !returnMode && String(paymentResult.paymentMethod || '').toLowerCase() !== 'card') {
      showToast('The bank discount is for card payment only — pay by CARD or remove the offer.', 'error');
      return;
    }
    const retBD = returnMode ? returnBankDiscRef.current : null;
    if ((bdApplied || retBD) && !isOnline()) {
      showToast('Bank card discount sales and their returns need internet — reconnect and try again.', 'error');
      return;
    }

    setBusySaving(true);

    const calcTotals = totals; // snapshot now
    const finalCustomerId = paymentResult.customerId || customerId;
    const finalCustomerName = paymentResult.customerName || customerName;
    const wasReturn = returnMode;
    const returnOrig = returnOriginalId;
    const tranTypeCode = wasReturn ? 'SR' : 'PS';
    const docDate = localDateStr(); // POS-06: Pakistan date, not UTC

    // Snapshot what we need for the receipt — so even if state is cleared,
    // the print path still has everything.
    // Show Two Units: second-unit qty worked out fresh for the receipt (dropped when not applicable).
    const snapshotLines = calcTotals.lines.map(({ secondUnitCode, secondQuantity, ...l }) => ({ ...l, ...(twoUnitLineFields(itemsById[l.itemId], l) || {}) }));

    // Bank discount on a SALE: amounts, shop/bank split and the card's new usage.
    let bankDiscountData = null;
    if (bdApplied) {
      const amt = calcTotals.bankDiscountAmount || 0;
      const u = bdApplied.usage || {};
      bankDiscountData = {
        offerId: bdApplied.offerId, label: bdApplied.label, bankName: bdApplied.bankName, cardType: bdApplied.cardType,
        percent: bdApplied.percent, cardFirst6: bdApplied.cardFirst6, cardLast4: bdApplied.cardLast4, cardMask: bdApplied.cardMask,
        amount: amt, ...splitShares(amt, bdApplied.shopSharePct),
        claimAccountId: bdApplied.claimAccountId, claimAccountCode: bdApplied.claimAccountCode, claimAccountName: bdApplied.claimAccountName,
        recoveryAccountId: bdApplied.recoveryAccountId, recoveryAccountCode: bdApplied.recoveryAccountCode, recoveryAccountName: bdApplied.recoveryAccountName,
        docDate,
        usage: {
          dayKey: u.dayKey || docDate, monthKey: u.monthKey || docDate.slice(0, 7),
          dayCount: (Number(u.dayCount) || 0) + 1, monthCount: (Number(u.monthCount) || 0) + 1,
          monthAmount: Math.round(((Number(u.monthAmount) || 0) + amt) * 100) / 100
        }
      };
    }
    // RETURN against a bank-discounted sale: give back the bank's share of the
    // discount on the returned quantity.
    let bankDiscountReturnData = null;
    if (retBD) {
      const returned = Math.round(snapshotLines.reduce((s, l) =>
        s + (Number(l._bankDiscPerUnit) || 0) * (Number(l.quantity) || 0), 0) * 100) / 100;
      if (returned > 0) {
        const sh = splitShares(returned, retBD.shopSharePct);
        bankDiscountReturnData = {
          originalTransactionId: retBD.transactionId, originalDocNumber: retBD.docNumber || '',
          offerId: retBD.offerId, label: retBD.label, bankName: retBD.bankName, cardMask: retBD.cardMask,
          amount: returned, ...sh,
          claimAccountId: retBD.claimAccountId, claimAccountCode: retBD.claimAccountCode, claimAccountName: retBD.claimAccountName,
          recoveryAccountId: retBD.recoveryAccountId, recoveryAccountCode: retBD.recoveryAccountCode, recoveryAccountName: retBD.recoveryAccountName
        };
      }
    }
    const receiptBank = bankDiscountData
      ? { label: bankDiscountData.label, percent: bankDiscountData.percent, amount: bankDiscountData.amount, cardMask: bankDiscountData.cardMask }
      : null;

    // Everything the save needs (the doc number is added once it is taken).
    const saleData = {
      cart: snapshotLines,
      totals: calcTotals,
      payments: routeCashToCounter(paymentResult.payments),
      paymentMethod: paymentResult.paymentMethod,
      customerId: finalCustomerId,
      customerName: finalCustomerName,
      financialYearId: activeFinancialYear.id,
      cashierName: userProfile?.displayName || userProfile?.fullName || 'Cashier',
      returnMode: wasReturn,
      returnOriginalId: returnOrig,
      branchId: activeCounter?.branchId || activeBranch?.id || null,
      branchName: activeCounter?.branchName || activeBranch?.branchName || null,
      // Deduct stock from THIS branch's assigned location (Branch Setup).
      locationId: activeBranch?.defaultLocationId || null,
      locationName: activeBranch?.defaultLocationName || null,
      counterId: activeCounter?.id || null,
      counterCode: activeCounter?.counterCode || null,
      shiftId: activeShift?.id || null,
      itemsCatalog: items,
      ...(bankDiscountData ? { bankDiscount: bankDiscountData } : {}),
      ...(bankDiscountReturnData ? { bankDiscountReturn: bankDiscountReturnData } : {})
    };

    // POS-01: run every check that could make the save fail (entries balance,
    // system accounts present, …) BEFORE a number is taken or a receipt
    // printed — pure work on the loaded caches, no Firestore read.
    try {
      preparePOSSale(companyId, saleData, currentUser.uid, cachesRef.current, { validateOnly: true });
    } catch (e) {
      console.error('[POS] Sale check failed:', e);
      showToast(`Cannot complete this ${wasReturn ? 'return' : 'sale'}: ${e.message}`, 'error');
      setBusySaving(false);
      return;
    }

    try {
      // 1) Pre-allocate doc number (~1 atomic transaction). Fast.
      const docNumber = await allocatePOSDocNumber(companyId, tranTypeCode, docDate, activeCounter?.branchId || activeBranch?.id || null, activeCounter);
      // The complete save (header + ledger + stock rows, with their ids).
      const saleDataNo = { ...saleData, preAllocatedDocNumber: docNumber };
      const prepared = preparePOSSale(companyId, saleDataNo, currentUser.uid, cachesRef.current);

      // 2) Optimistic UI: print receipt + clear cart + decrement stock RIGHT NOW.
      printReceipt({
        docNumber,
        docDate,
        cart: snapshotLines,
        subtotal: calcTotals.subtotal,
        discountAmount: calcTotals.totalDiscount,
        taxAmount: calcTotals.taxAmount,
        totalAmount: calcTotals.totalAmount,
        paymentMethod: paymentResult.paymentMethod,
        payments: paymentResult.payments,
        cashTendered: paymentResult.cashTendered || 0,
        changeGiven: paymentResult.changeGiven || 0,
        customerName: finalCustomerName,
        cashierName: userProfile?.displayName || userProfile?.fullName || 'Cashier',
        previousBalance: (!wasReturn && !isWalkIn) ? customerBalance : null,
        ...(receiptBank ? { bankDiscount: receiptBank } : {})
      }, activeCompany, posReceiptCfg);

      // Stash for Reprint / Repeat
      lastSaleRef.current = {
        docNumber, docDate, cart: snapshotLines,
        subtotal: calcTotals.subtotal,
        discountAmount: calcTotals.totalDiscount,
        taxAmount: calcTotals.taxAmount,
        totalAmount: calcTotals.totalAmount,
        paymentMethod: paymentResult.paymentMethod,
        payments: paymentResult.payments,
        customerId: finalCustomerId,
        customerName: finalCustomerName,
        previousBalance: (!wasReturn && !isWalkIn) ? customerBalance : null,
        ...(receiptBank ? { bankDiscount: receiptBank } : {})
      };

      // Decrement stock for sales, increment for returns
      decrementStockLocally(snapshotLines, wasReturn ? +1 : -1);

      // Reset UI for next sale immediately
      clearCart();
      setBusySaving(false);
      showToast(`Receipt ${docNumber} printed`, 'success');

      // Keep the running balance correct if the same customer is retained for the
      // next sale: only the unpaid (credit) portion adds to what they owe.
      if (!wasReturn && !isWalkIn && customerBalance != null) {
        const paidNonCredit = Math.min(
          (paymentResult.payments || [])
            .filter(p => String(p.method || '').toLowerCase() !== 'credit')
            .reduce((s, p) => s + (Number(p.amount) || 0), 0),
          calcTotals.totalAmount || 0
        );
        const creditPortion = (calcTotals.totalAmount || 0) - paidNonCredit;
        setCustomerBalance(b => (b == null ? b : b + creditPortion));
      }

      // 3) Background save — no await blocking the UI. If it fails, the SAME
      //    sale (same id) is kept in the offline outbox and uploaded later —
      //    a printed sale is never dropped. Only if even that fails is the
      //    local stock change reverted and the cashier told to re-enter it.
      const label = wasReturn ? 'Return' : 'Sale';
      savePreparedPOSSale(companyId, saleDataNo, currentUser.uid, cachesRef.current, prepared)
        .then((out) => {
          try {
            if (out.status === 'saved') {
              if (bankDiscountReturnData && retBD) {
                reverseBankDiscountUsage(companyId, retBD, {
                  amount: bankDiscountReturnData.amount,
                  full: bankDiscountReturnData.amount >= (Number(retBD.amount) || 0) - 0.01,
                  transactionId: retBD.transactionId,
                  returnDocNumber: docNumber
                });
              }
              foldSaleIntoStockCache(out.result);
              return;
            }
            // Kept in the outbox: stock stays as sold (the sale WILL be saved).
            foldSaleIntoStockCache(null);
            reportQueuedSale(label, docNumber, out);
          } catch (e) { console.warn('[POS] post-save step failed:', e); }
        }, (err) => {
          console.error('[POS] Background save failed:', err);
          decrementStockLocally(snapshotLines, wasReturn ? -1 : +1);
          reportUnsavedSale(label, docNumber, err);
        });
    } catch (error) {
      console.error('[POS] Doc number allocation failed:', error);
      showToast(`Save failed: ${error.message}`, 'error');
      setBusySaving(false);
    }
  }, [canEdit, companyId, activeFinancialYear, currentUser, totals, cart, customerId, customerName, returnMode, returnOriginalId,
      clearCart, items, itemsById, stockBalances, activeCompany, userProfile, activeBranch,
      decrementStockLocally, foldSaleIntoStockCache,
      showToast, isModuleEnabled,
      activeCounter, activeShift, exchangeMode, handleExchangePay, routeCashToCounter, customerBalance, isWalkIn,
      bankDiscount, posUserInactive, reportQueuedSale, reportUnsavedSale]);

  // ── Void ──
  const handleVoid = useCallback(() => {
    if (!canEdit) { showToast('No permission to void', 'error'); return; }
    if (cart.length === 0) return;
    if (window.confirm('Void current sale?')) clearCart();
  }, [canEdit, cart, clearCart, showToast]);

  // ── Helpers: apply a picked receipt to Return or Exchange state ────────
  const applyReceiptAsReturn = useCallback((original) => {
    setReturnMode(true);
    setReturnOriginalId(original.id);
    setReturnOriginalDoc(original.docNumber);
    if (original.lines && original.lines.length > 0) {
      const payFactor = receiptPayFactor(original);
      setCart(original.lines.map(line => withReturnBasis({
        itemId: line.itemId,
        itemCode: line.itemCode || '',
        itemName: line.itemName || '',
        description: line.description || '',
        quantity: line.quantity || 0,
        unitPrice: line.unitPrice || 0,
        unitCost: line.unitCost || 0,
        discountPercent: line.discountPercent || 0,
        discountAmount: line.discountAmount || 0,
        // Carry the original line's tax so the return reverses the same GST.
        taxable: line.taxable !== false,
        taxRate: Number(line.taxRate) || 0,
        unitOfMeasure: line.unitOfMeasure || 'Pcs',
        unitCode: line.unitCode || line.unitOfMeasure || '',
        ...bankReturnLineFields(line)
      }, line, payFactor)));
    }
    returnBankDiscRef.current = bankReturnStash(original);
    if (original.partyId) {
      setCustomerId(original.partyId);
      setCustomerName(original.partyName || 'Walk-in Customer');
    }
  }, []);

  const applyReceiptAsExchange = useCallback((original) => {
    const lines = (original.lines || []).map(line => ({
      itemId: line.itemId,
      itemCode: line.itemCode || '',
      itemName: line.itemName || '',
      description: line.description || '',
      quantity: line.quantity || 0,
      unitPrice: line.unitPrice || 0,
      unitCost: line.unitCost || 0,
      discountPercent: line.discountPercent || 0,
      discountAmount: line.discountAmount || 0,
      taxable: line.taxable !== false,
      taxRate: Number(line.taxRate) || 0,
      lineTotal: line.lineTotal || (Number(line.quantity || 0) * Number(line.unitPrice || 0)),
      unitOfMeasure: line.unitOfMeasure || 'Pcs',
      unitCode: line.unitCode || line.unitOfMeasure || '',
      _selected: false,
      returnQty: line.quantity || 0
    }));
    setExchangeMode(true);
    setExchangeOriginalId(original.id);
    setExchangeOriginalDoc(original.docNumber);
    setExchangeReturnLines(lines);
    if (original.partyId) {
      setCustomerId(original.partyId);
      setCustomerName(original.partyName || 'Walk-in Customer');
    }
  }, []);

  // ── Return Mode ──
  const handleToggleReturn = useCallback(() => {
    if (!canEdit) { showToast('No permission to process returns', 'error'); return; }
    if (returnMode) {
      setReturnMode(false); setReturnOriginalId(null); setReturnOriginalDoc(''); clearCart();
      return;
    }
    setPickerMode('return');
  }, [canEdit, returnMode, clearCart, showToast]);

  // ── Exchange Mode ──
  const handleStartExchange = useCallback(() => {
    if (!canEdit) { showToast('No permission to process exchanges', 'error'); return; }
    if (exchangeMode) {
      // Toggle off
      setExchangeMode(false);
      setExchangeOriginalId(null);
      setExchangeOriginalDoc('');
      setExchangeReturnLines([]);
      return;
    }
    setPickerMode('exchange');
  }, [canEdit, exchangeMode, showToast]);

  // ── Picker → apply ──
  const handlePickerSelect = useCallback((txn) => {
    const mode = pickerMode;
    setPickerMode(null);
    if (!txn) return;
    if (mode === 'return') {
      applyReceiptAsReturn(txn);
      showToast(`Return mode — receipt ${txn.docNumber} loaded`, 'info');
    } else if (mode === 'exchange') {
      if (txn.bankDiscount) {
        showToast(`Receipt ${txn.docNumber} has a bank card discount — use RETURN instead of Exchange.`, 'error');
        return;
      }
      applyReceiptAsExchange(txn);
      showToast(`Exchange opened against ${txn.docNumber} — tick the items to return and add new items below.`, 'info');
    }
  }, [pickerMode, applyReceiptAsReturn, applyReceiptAsExchange, showToast]);

  const toggleExchangeLine = useCallback((idx) => {
    setExchangeReturnLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], _selected: !next[idx]._selected };
      return next;
    });
  }, []);

  const updateExchangeQty = useCallback((idx, value) => {
    setExchangeReturnLines(prev => {
      const next = [...prev];
      const v = Math.max(0, Math.min(Number(value) || 0, Number(next[idx].quantity) || 0));
      next[idx] = { ...next[idx], returnQty: v, _selected: v > 0 };
      return next;
    });
  }, []);

  // ── History Actions ──
  const handleHistoryReprint = useCallback((txn) => {
    printReceipt({
      docNumber: txn.docNumber, docDate: txn.docDate,
      lines: txn.lines || [],
      subtotal: txn.subtotal || 0,
      discountAmount: txn.discountAmount || 0,
      taxAmount: txn.taxAmount || 0,
      totalAmount: txn.totalAmount || 0,
      paymentMethod: txn.paymentMethod,
      payments: txn.posPayments || [],
      cashTendered: 0, changeGiven: 0,
      customerName: txn.partyName || 'Walk-in Customer',
      cashierName: txn.cashierName || '',
      ...(txn.bankDiscount ? { bankDiscount: txn.bankDiscount } : {})
    }, activeCompany, posReceiptCfg);
  }, [activeCompany, posReceiptCfg]);

  // Hard-void a posted POS sale: status → Cancelled, drop stockFlow (stock
  // returns to inventory), drop subTransactions (accounting reverses). Unlike
  // Return, this does NOT create a new SR — it cancels the original PS.
  const handleHistoryVoid = useCallback(async (txn) => {
    if (!canEdit) { showToast('No permission to void', 'error'); return; }
    if (!txn?.id) return;
    if (txn.status === 'Cancelled') { showToast('Already voided', 'info'); return; }
    if (!window.confirm(`Void POS sale ${txn.docNumber}?\n\nThis will:\n• Reverse stock (items return to inventory)\n• Reverse accounting (cash/receivable + COGS + sales)\n• Mark the sale as VOIDED in reports\n\nCannot be undone.`)) return;
    try {
      await deleteStockFlow(activeCompany.id, txn.id);
      await deleteSubTransactions(activeCompany.id, txn.id);
      await updateTransactionStatus(activeCompany.id, txn.id, 'Cancelled', currentUser.uid);
      // Restore local stock cache: each line's qty goes back into inventory.
      // Routed through decrementStockLocally so the PERSISTED copy and the
      // cached badge are corrected too — a void used to leave both showing the
      // pre-void numbers until the cache expired.
      decrementStockLocally(txn.lines || [], +1);
      if (txn.bankDiscount && txn.tranTypeCode !== 'SR') {
        reverseBankDiscountUsage(activeCompany.id, txn.bankDiscount, {
          amount: Number(txn.bankDiscount.amount) || 0, full: true, transactionId: txn.id, voided: true
        });
      }
      showToast(`Voided ${txn.docNumber} — stock & accounting reversed`, 'success');
      setShowHistoryModal(false);
      setTimeout(() => setShowHistoryModal(true), 100); // re-open to refresh list
    } catch (e) {
      console.error('Void error:', e);
      showToast('Void failed: ' + (e.message || 'error'), 'error');
    }
  }, [canEdit, activeCompany?.id, currentUser?.uid, decrementStockLocally, showToast]);

  // Delete a bill — opens the admin-password modal (verification happens on confirm).
  const handleHistoryDelete = useCallback((txn) => {
    if (!canEdit) { showToast('No permission', 'error'); return; }
    if (!txn?.id) return;
    setDeleteBillTarget(txn);
    setDeletePassword('');
  }, [canEdit, showToast]);

  const confirmDeleteBill = useCallback(async () => {
    const txn = deleteBillTarget;
    if (!txn) return;
    if (!deletePassword) { showToast('Enter the admin password', 'error'); return; }
    setDeleteBusy(true);
    try {
      const ok = await verifyAdminPassword({
        companyId: activeCompany?.id, currentUser, companyUser, activeCompany, password: deletePassword,
      });
      if (!ok) { showToast('Incorrect admin password', 'error'); setDeleteBusy(false); return; }
      // Permanently remove the bill + its stock & accounting, AND cascade-delete
      // any payment(s) received against it. (Blocks only if a linked payment also
      // covers other invoices, or the bill has a linked return.)
      const delRes = await deletePOSBillWithPayments(activeCompany.id, txn.id);
      // Restore local stock cache: a deleted SALE returns stock; a deleted RETURN
      // removes it. Same helper as a sale, so the persisted copy stays in step.
      const sign = txn.tranTypeCode === 'SR' ? -1 : +1;
      decrementStockLocally(txn.lines || [], sign);
      if (txn.bankDiscount && txn.tranTypeCode !== 'SR') {
        reverseBankDiscountUsage(activeCompany.id, txn.bankDiscount, {
          amount: Number(txn.bankDiscount.amount) || 0, full: true, transactionId: txn.id, voided: true
        });
      }
      showToast(
        `Deleted ${txn.docNumber}${delRes?.deletedPayments ? ` + ${delRes.deletedPayments} linked payment(s)` : ''} — stock & accounting reversed`,
        'success'
      );
      setDeleteBillTarget(null);
      setDeletePassword('');
      setShowHistoryModal(false);
      setTimeout(() => setShowHistoryModal(true), 100); // re-open to refresh list
    } catch (e) {
      console.error('Delete bill error:', e);
      showToast('Delete failed: ' + (e.message || 'error'), 'error');
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteBillTarget, deletePassword, activeCompany, currentUser, companyUser, decrementStockLocally, showToast]);

  const handleHistoryReturn = useCallback((txn) => {
    setShowHistoryModal(false);
    setReturnMode(true);
    setReturnOriginalId(txn.id);
    setReturnOriginalDoc(txn.docNumber);
    if (txn.lines && txn.lines.length > 0) {
      const payFactor = receiptPayFactor(txn);
      setCart(txn.lines.map(line => withReturnBasis({
        itemId: line.itemId,
        itemCode: line.itemCode || '',
        itemName: line.itemName || '',
        description: line.description || '',
        quantity: line.quantity || 0,
        unitPrice: line.unitPrice || 0,
        unitCost: line.unitCost || 0,
        discountPercent: line.discountPercent || 0,
        discountAmount: line.discountAmount || 0,
        taxable: line.taxable !== false, taxRate: Number(line.taxRate) || 0,
        unitOfMeasure: line.unitOfMeasure || 'Pcs',
        unitCode: line.unitCode || line.unitOfMeasure || '',
        ...bankReturnLineFields(line)
      }, line, payFactor)));
    }
    returnBankDiscRef.current = bankReturnStash(txn);
    if (txn.partyId) {
      setCustomerId(txn.partyId);
      setCustomerName(txn.partyName || 'Walk-in Customer');
    }
  }, []);

  // ── Parked sales (in-memory hold/recall) ──
  const handleHold = useCallback(() => {
    if (cart.length === 0) return;
    const parked = {
      id: Date.now(),
      label: customerName + ' · ' + formatCurrency(totals.totalAmount),
      cart: cart.map(c => ({ ...c })),
      customerId, customerName, headerDiscount,
      returnMode, returnOriginalId, returnOriginalDoc,
      returnBankDisc: returnMode ? returnBankDiscRef.current : null,
      ts: new Date().toISOString()
    };
    setParkedSales(prev => [parked, ...prev].slice(0, 10));
    clearCart();
    showToast('Sale parked', 'success');
  }, [cart, customerId, customerName, totals.totalAmount, headerDiscount, returnMode, returnOriginalId, returnOriginalDoc, clearCart, formatCurrency, showToast]);

  const handleRecallParked = useCallback((parked) => {
    setCart(parked.cart);
    setCustomerId(parked.customerId);
    setCustomerName(parked.customerName);
    setHeaderDiscount(parked.headerDiscount || 0);
    if (parked.returnMode) {
      setReturnMode(true);
      setReturnOriginalId(parked.returnOriginalId);
      setReturnOriginalDoc(parked.returnOriginalDoc || '');
      returnBankDiscRef.current = parked.returnBankDisc || null;
    }
    setParkedSales(prev => prev.filter(p => p.id !== parked.id));
    setShowParkedDropdown(false);
  }, []);

  // ── Reprint / Repeat last ──
  const handleReprintLast = useCallback(() => {
    if (!lastSaleRef.current) { showToast('No recent sale to reprint', 'error'); return; }
    printReceipt(lastSaleRef.current, activeCompany, posReceiptCfg);
  }, [activeCompany, posReceiptCfg, showToast]);

  const handleRepeatLast = useCallback(() => {
    if (!lastSaleRef.current) { showToast('No recent sale to repeat', 'error'); return; }
    if (cart.length > 0 && !window.confirm('Replace current cart with last sale?')) return;
    setCart(lastSaleRef.current.cart.map(c => ({ ...c })));
    if (lastSaleRef.current.customerId) {
      setCustomerId(lastSaleRef.current.customerId);
      setCustomerName(lastSaleRef.current.customerName);
    }
    showToast('Loaded last sale', 'info');
  }, [cart, showToast]);

  const handleDuplicateLine = useCallback(() => {
    if (selectedCartIndex < 0) return;
    const line = cart[selectedCartIndex];
    if (!line) return;
    setCart(prev => [...prev, { ...line, quantity: 1 }]);
    setSelectedCartIndex(cart.length);
  }, [cart, selectedCartIndex]);

  // ── Header discount prompt ──
  const handleHeaderDiscount = useCallback(() => {
    const d = window.prompt('Header discount amount:', headerDiscount);
    if (d !== null) setHeaderDiscount(Number(d) || 0);
  }, [headerDiscount]);

  // ── Keyboard Shortcuts ──
  usePOSKeyboard({
    onSearch: () => searchInputRef.current?.focus(),
    onCustomer: () => setShowCustomerDropdown(true),
    onHold: handleHold,
    onRecall: () => setShowParkedDropdown(true),
    onPay: () => {
      // Phase-1: F5 triggers the focused-method action on the bar. The bar
      // listens for Space — simulate that here.
      if (cart.length > 0 && !busySaving) {
        const ev = new KeyboardEvent('keydown', { key: ' ', bubbles: true });
        window.dispatchEvent(ev);
      }
    },
    onSplit: () => { /* PaymentBar handles via chip — no-op fallback */ },
    onHistory: () => setShowHistoryModal(true),
    onVoid: handleVoid,
    onReturn: handleToggleReturn,
    onExchange: handleStartExchange,
    onDiscount: handleHeaderDiscount,
    onEscape: () => {
      if (saveAlerts.length > 0) return; // POS-01: the cashier must acknowledge it
      if (pickerMode) setPickerMode(null);
      else if (showHistoryModal) setShowHistoryModal(false);
      else if (showCustomerDropdown) { setShowCustomerDropdown(false); setCustomerSearch(''); }
      else if (showParkedDropdown) setShowParkedDropdown(false);
      else if (editTarget) setEditTarget(null);
      else navigate('/dashboard');
    },
    onIncrementQty: () => {
      if (selectedCartIndex >= 0) updateCartItem(selectedCartIndex, { quantity: (cart[selectedCartIndex]?.quantity || 0) + 1 });
    },
    onDecrementQty: () => {
      if (selectedCartIndex >= 0) {
        const q = cart[selectedCartIndex]?.quantity || 0;
        if (q > 1) updateCartItem(selectedCartIndex, { quantity: q - 1 });
      }
    },
    onRemoveLine: () => { if (selectedCartIndex >= 0) removeCartItem(selectedCartIndex); },
    onCartUp: () => setSelectedCartIndex(i => Math.max(0, i - 1)),
    onCartDown: () => setSelectedCartIndex(i => Math.min(cart.length - 1, i + 1)),
    onReprintLast: handleReprintLast,
    onRepeatLast: handleRepeatLast,
    onDuplicateLine: handleDuplicateLine
  }, true);

  // ── Render ──
  if (loading) {
    return <LoadingScreen><LoadingText>Loading POS…</LoadingText></LoadingScreen>;
  }

  return (
    <FullScreen>
      {auditLocked && (
        <AuditLockBar>
          🔒 STOCK COUNT IN PROGRESS — selling is paused at this location until the count is submitted.
        </AuditLockBar>
      )}
      {/* LEFT PANEL */}
      <LeftPanel>
        <TopBar>
          <TopBarLeft>
            <CounterTitle>
              <LogoText>{activeCounter?.counterName || 'AMAL POS'}</LogoText>
              {activeCounter?.branchName && <BranchText>{activeCounter.branchName}</BranchText>}
            </CounterTitle>
            <ClockText>{clockTime.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}</ClockText>
            <POSOfflineIndicator />
            {activeCounter && (activeShift ? (
              <ShiftBadge>
                <ShiftBadgeText>Shift {activeShift.shiftNumber}</ShiftBadgeText>
                <ShiftActionBtn onClick={handleOpenCloseModal} disabled={shiftBusy} title="Close shift & print Z-report">
                  Close Shift
                </ShiftActionBtn>
              </ShiftBadge>
            ) : shiftChecked ? (
              <ShiftActionBtn $open onClick={handleShowOpenShift} disabled={shiftBusy} title="Open shift to start selling">
                Open Shift
              </ShiftActionBtn>
            ) : null)}
          </TopBarLeft>
          <TopBarRight>
            <MoreBtn onClick={() => setMobileMoreOpen(o => !o)} title="More actions">
              <FiMoreHorizontal size={14} /> More
            </MoreBtn>
            <SecondaryControls $open={mobileMoreOpen}>
              {priceTypeEnabled && (
                <PriceTypeWrap title="Price list for newly added items">
                  <PriceTypeLabel>Price</PriceTypeLabel>
                  <PriceTypeSelect value={priceType} onChange={e => setPriceType(e.target.value)}>
                    <option value="Retail">Retail</option>
                    <option value="Wholesale">Wholesale</option>
                    <option value="Dealer">Dealer</option>
                  </PriceTypeSelect>
                </PriceTypeWrap>
              )}
              <ParkedWrap ref={parkedRef}>
                <TopBtn onClick={() => setShowParkedDropdown(s => !s)} title="F4: Recall">
                  <FiPlay size={13} /> Recall {parkedSales.length > 0 && `(${parkedSales.length})`}
                </TopBtn>
                {showParkedDropdown && (
                  <ParkedDropdown>
                    {parkedSales.length === 0 ? (
                      <ParkedEmpty>No parked sales</ParkedEmpty>
                    ) : parkedSales.map(p => (
                      <ParkedItem key={p.id} onClick={() => { handleRecallParked(p); setMobileMoreOpen(false); }}>
                        <span>{p.label}</span>
                        <small>{new Date(p.ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}</small>
                      </ParkedItem>
                    ))}
                  </ParkedDropdown>
                )}
              </ParkedWrap>
              <ReturnToggle $active={returnMode} onClick={() => { handleToggleReturn(); setMobileMoreOpen(false); }} title="F9: Return">
                <FiRotateCcw size={13} /> Return
              </ReturnToggle>
              <ReturnToggle $active={exchangeMode} onClick={() => { handleStartExchange(); setMobileMoreOpen(false); }} title="F10: Exchange">
                <FiRepeat size={13} /> Exchange
              </ReturnToggle>
            </SecondaryControls>
            <MoreBackdrop $open={mobileMoreOpen} onClick={() => setMobileMoreOpen(false)} />
            <ExitButton onClick={() => navigate('/dashboard')} title="Esc: Exit">
              <FiX size={18} />
            </ExitButton>
          </TopBarRight>
        </TopBar>

        {returnMode && (
          <ReturnBanner>RETURN MODE — Receipt: {returnOriginalDoc}</ReturnBanner>
        )}
        {exchangeMode && (
          <ExchangeBanner>
            EXCHANGE MODE — Against: {exchangeOriginalDoc}
            <span style={{ marginLeft: 12, fontWeight: 400 }}>
              tick lines below to return, then add new items in the cart
            </span>
          </ExchangeBanner>
        )}

        <CustomerBar ref={customerDropdownRef} $mstep={mobileStep}>
          <CustomerDisplay onClick={() => setShowCustomerDropdown(s => !s)}>
            <FiUser size={14} />
            <CustomerNameText $isWalkIn={isWalkIn}>{customerName}</CustomerNameText>
            <FiChevronDown size={14} />
          </CustomerDisplay>
          {!isWalkIn && (
            <ResetCustomerBtn onClick={() => handleSelectCustomer(null)} title="Reset to Walk-in">
              <FiX size={14} />
            </ResetCustomerBtn>
          )}
          <AddCustomerBtn
            onClick={() => { setNewCustPhone(''); setNewCustName(''); setShowCustomerDropdown(false); setShowAddCustomer(true); }}
            title="Add / find customer by phone"
          >
            <FiPlus size={14} /> Add
          </AddCustomerBtn>
          {showCustomerDropdown && (
            <CustomerDropdown>
              <CustomerSearchInput value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search customer…" autoFocus />
              <CustomerList>
                {walkInCustomer && (
                  <CustomerOption $active={isWalkIn} onClick={() => handleSelectCustomer(null)}>
                    <CustomerOptName>Walk-in Customer</CustomerOptName>
                    <CustomerOptTag>Default</CustomerOptTag>
                  </CustomerOption>
                )}
                {filteredCustomers.filter(c => !c.isWalkIn).map(c => (
                  <CustomerOption key={c.id} $active={customerId === c.id} onClick={() => handleSelectCustomer(c)}>
                    <CustomerOptName>{c.companyName || c.customerName || 'Unnamed'}</CustomerOptName>
                  </CustomerOption>
                ))}
              </CustomerList>
            </CustomerDropdown>
          )}
        </CustomerBar>

        <MidArea $mstep={mobileStep}>
        {exchangeMode && exchangeReturnLines.length > 0 && (
          <ExchangePanel>
            <ExchangePanelHead>
              <strong>RETURNING from {exchangeOriginalDoc}</strong>
              <ReasonSelect value={exchangeReason} onChange={e => setExchangeReason(e.target.value)}>
                <option>Customer Change</option>
                <option>Damaged</option>
                <option>Wrong Item</option>
                <option>Expired</option>
                <option>Other</option>
              </ReasonSelect>
            </ExchangePanelHead>
            <ExchangeScroll>
              {exchangeReturnLines.map((line, idx) => {
                const origQty = Number(line.quantity) || 0;
                const perUnit = origQty > 0 ? (Number(line.lineTotal) || origQty * line.unitPrice) / origQty : 0;
                const rowTotal = (Number(line.returnQty) || 0) * perUnit;
                return (
                  <ExchangeRow key={idx} $selected={line._selected}>
                    <input
                      type="checkbox"
                      checked={!!line._selected}
                      onChange={() => toggleExchangeLine(idx)}
                    />
                    <ExName>
                      {line.itemCode && <ExCode>{line.itemCode}</ExCode>}
                      {line.itemName}
                      <small>orig {origQty} × {formatCurrency(line.unitPrice)}</small>
                    </ExName>
                    <ExQty
                      type="number"
                      min={0}
                      max={origQty}
                      value={line.returnQty}
                      onChange={e => updateExchangeQty(idx, e.target.value)}
                      disabled={!line._selected}
                    />
                    <ExTotal>{formatCurrency(rowTotal)}</ExTotal>
                  </ExchangeRow>
                );
              })}
            </ExchangeScroll>
            <ExchangeSummary>
              <span>Return value</span>
              <strong>-{formatCurrency(exchangeReturnTotal)}</strong>
            </ExchangeSummary>
          </ExchangePanel>
        )}

        {weightScaleEnabled && (
          <WeightScaleWidget onCapture={handleWeightCapture} captureLabel={weightCaptureLabel} />
        )}

        <CartContainer>
          <CartHeader>
            <CartHeaderCell $flex={0.5}>#</CartHeaderCell>
            <CartHeaderCell $flex={3}>Item</CartHeaderCell>
            <CartHeaderCell $flex={1.1}>Qty</CartHeaderCell>
            <CartHeaderCell $flex={1.2}>Price</CartHeaderCell>
            <CartHeaderCell $flex={1.2}>Total</CartHeaderCell>
            <CartHeaderCell $flex={0.5}></CartHeaderCell>
          </CartHeader>
          <CartScrollArea>
            {cart.length === 0 ? (
              <EmptyCart>{returnMode ? 'No return items loaded' : 'Scan barcode, type to search, or pick a product'}</EmptyCart>
            ) : (
              cart.map((item, idx) => {
                const calculated = calculatePOSLine(item, 'exclusive');
                return (
                  <CartRow key={idx} $selected={selectedCartIndex === idx} onClick={() => setSelectedCartIndex(idx)}>
                    <CartCell $flex={0.5}>{idx + 1}</CartCell>
                    <CartCell $flex={3}>
                      <ItemName>{item.itemName}</ItemName>
                      {(item.itemCode || item.barcode) && (
                        <CartBarcode>
                          {[item.itemCode, item.barcode].filter(Boolean).join('  ·  ')}
                        </CartBarcode>
                      )}
                      <DiscChip
                        $hasDisc={(item.discountPercent || 0) > 0 || (item.discountAmount || 0) > 0}
                        onClick={(e) => { e.stopPropagation(); openLineEditor(idx, 'discount'); }}
                        title="Click to set discount"
                      >
                        {(item.discountPercent || 0) > 0
                          ? `-${item.discountPercent}%`
                          : (item.discountAmount || 0) > 0
                            ? `-${formatCurrency(item.discountAmount)}`
                            : '+ Add discount'}
                      </DiscChip>
                    </CartCell>
                    <CartCell $flex={1.1}>
                      <QtyControls>
                        <QtyBtn onClick={(e) => { e.stopPropagation(); updateCartItem(idx, { quantity: Math.max(1, item.quantity - 1) }); }}>
                          <FiMinus size={12} />
                        </QtyBtn>
                        <QtyValueBtn onClick={(e) => { e.stopPropagation(); openLineEditor(idx, 'qty'); }} title="Click to enter qty">
                          {secondUnitForLine(itemsById[item.itemId], item.unitCode || item.unitOfMeasure) ? formatTwoUnitQty(item.quantity) : item.quantity}
                        </QtyValueBtn>
                        <QtyBtn onClick={(e) => { e.stopPropagation(); updateCartItem(idx, { quantity: item.quantity + 1 }); }}>
                          <FiPlus size={12} />
                        </QtyBtn>
                      </QtyControls>
                      {(() => {
                        const itm = items.find(i => i.id === item.itemId);
                        const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                        if (multiUnitEnabled && itmUnits.length > 1) {
                          return (
                            <UnitPicker
                              value={item.unitCode || item.unitOfMeasure || ''}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => changeCartItemUnit(idx, e.target.value)}
                            >
                              {itmUnits.map(u => <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>)}
                            </UnitPicker>
                          );
                        }
                        return null;
                      })()}
                      {(() => {
                        // Show Two Units: same qty in the item's other unit; tap to type it.
                        const twoItem = itemsById[item.itemId];
                        const lineUnit = item.unitCode || item.unitOfMeasure;
                        const second = secondUnitForLine(twoItem, lineUnit);
                        if (!second) return null;
                        return (
                          <TwoUnitChip
                            data-two-unit-qty={second}
                            onClick={(e) => { e.stopPropagation(); openLineEditor(idx, 'qty2'); }}
                            title={`Click to enter qty in ${second}`}
                          >
                            = {formatTwoUnitQty(secondQtyFromQty(twoItem, item.quantity, lineUnit, second))} {second}
                          </TwoUnitChip>
                        );
                      })()}
                    </CartCell>
                    <CartCell $flex={1.2}>
                      <PriceBtn onClick={(e) => { e.stopPropagation(); openLineEditor(idx, 'price'); }} title="Click to enter price">
                        {formatCurrency(item.unitPrice)}
                      </PriceBtn>
                      {priceTypeEnabled && (
                        <LinePriceType
                          value={item.priceType || 'Retail'}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => changeCartItemPriceType(idx, e.target.value)}
                          title="Price type for this item"
                        >
                          <option value="Retail">Retail</option>
                          <option value="Wholesale">W.Sale</option>
                          <option value="Dealer">Dealer</option>
                        </LinePriceType>
                      )}
                    </CartCell>
                    <CartCell $flex={1.2}><TotalText>{formatCurrency(calculated.lineTotal)}</TotalText></CartCell>
                    <CartCell $flex={0.5}>
                      <RemoveItemBtn onClick={(e) => { e.stopPropagation(); removeCartItem(idx); }}>
                        <FiTrash2 size={13} />
                      </RemoveItemBtn>
                    </CartCell>
                  </CartRow>
                );
              })
            )}
          </CartScrollArea>
        </CartContainer>
        </MidArea>

        <TotalsSection $mstep={mobileStep}>
          <TotalRow>
            <span>{exchangeMode ? 'New items subtotal' : 'Subtotal'}</span>
            <span>{formatCurrency(totals.subtotal)}</span>
          </TotalRow>
          {totals.totalDiscount > 0 && (
            <TotalRow $muted>
              <span>Discount</span>
              <span>-{formatCurrency(totals.totalDiscount)}</span>
            </TotalRow>
          )}
          {bankDiscount && (totals.bankDiscountAmount || 0) > 0 && (
            <TotalRow $muted>
              <span>incl. {bankDiscount.label} {bankDiscount.percent}%</span>
              <span>-{formatCurrency(totals.bankDiscountAmount)}</span>
            </TotalRow>
          )}
          {exchangeMode && exchangeReturnTotal > 0 && (
            <TotalRow $muted>
              <span>Less return</span>
              <span>-{formatCurrency(exchangeReturnTotal)}</span>
            </TotalRow>
          )}
          <GrandTotalRow>
            <span>{exchangeMode ? (exchangeNet >= 0 ? 'NET DUE' : 'REFUND DUE') : 'TOTAL'}</span>
            <span>{formatCurrency(Math.abs(exchangeMode ? exchangeNet : totals.totalAmount))}</span>
          </GrandTotalRow>
          <ItemCountRow>
            {cart.length} item{cart.length !== 1 ? 's' : ''} · {cart.reduce((s, i) => s + i.quantity, 0)} units
            {exchangeMode && exchangeReturnLines.some(l => l._selected) && (
              <> · {exchangeReturnLines.filter(l => l._selected).length} returning</>
            )}
          </ItemCountRow>
        </TotalsSection>

        <MobilePayWrap $mstep={mobileStep}>
        <PaymentBar
          totalAmount={exchangeMode ? exchangePayable : totals.totalAmount}
          paymentAccounts={paymentAccounts}
          cashTendered={cashTendered}
          onCashTenderedChange={setCashTendered}
          isWalkInCustomer={isWalkIn}
          onComplete={handlePaymentComplete}
          disabled={!canEdit || (cart.length === 0 && !exchangeMode) || saveAlerts.length > 0}
          busy={busySaving}
          customerId={customerId}
          customerName={customerName}
          bankOffers={bankDiscountsEnabled && !returnMode && !exchangeMode ? validBankOffers : []}
          bankDiscount={bankDiscount ? { ...bankDiscount, amount: totals.bankDiscountAmount || 0 } : null}
          bankDiscountNote={bankDiscountsEnabled && !posOnline ? 'Bank offers need internet' : ''}
          onApplyBankDiscount={handleApplyBankDiscount}
          onClearBankDiscount={handleClearBankDiscount}
        />
        </MobilePayWrap>
        {exchangeMode && exchangeRefundable > 0 && (
          <RefundHint>
            Refund {formatCurrency(exchangeRefundable)} to customer — pick CASH / BANK / WALLET on the bar and press PAY.
          </RefundHint>
        )}
      </LeftPanel>

      {/* VERTICAL ACTION RAIL — sits between cart and product grid so
          secondary buttons don't eat the left panel's vertical space. */}
      <ActionRail $mstep={mobileStep}>
        <RailBtn onClick={handleVoid} disabled={cart.length === 0} title="F8: Void">
          <FiX size={20} />
          <RailLbl>VOID</RailLbl>
        </RailBtn>
        <RailBtn onClick={handleHeaderDiscount} disabled={cart.length === 0} title="F11: Header discount">
          <FiPercent size={20} />
          <RailLbl>DISC</RailLbl>
          {headerDiscount > 0 && <RailSub>{formatCurrency(headerDiscount)}</RailSub>}
        </RailBtn>
        <RailBtn onClick={handleHold} disabled={cart.length === 0} title="F3: Hold sale">
          <FiPause size={20} />
          <RailLbl>HOLD</RailLbl>
        </RailBtn>
        <RailBtn onClick={() => setShowHistoryModal(true)} title="F7: History">
          <FiList size={20} />
          <RailLbl>HISTORY</RailLbl>
        </RailBtn>
      </ActionRail>

      {/* RIGHT PANEL */}
      <RightPanel $mstep={mobileStep}>
        <SearchBar>
          <FiSearch size={18} />
          <SearchInput
            ref={searchInputRef}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder='Scan, type to search, or 5*name · #code · =price · +disc · -10%'
            autoFocus
          />
          {searchInput && (
            <ClearSearch onClick={() => setSearchInput('')}><FiX size={16} /></ClearSearch>
          )}
          <ScanBtn onClick={() => setShowScanner(true)} title="Scan with camera">
            <FiCamera size={20} />
          </ScanBtn>
        </SearchBar>

        <ProductGridWrapper>
          <POSProductGrid
            items={filterItemsByPricing(items, customerPricing)}
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onCategorySelect={setSelectedCategoryId}
            onItemAdd={addToCart}
            stockBalances={stockBalances}
            searchQuery={searchInput}
          />
        </ProductGridWrapper>

        <HintBar>
          <Hint>F1 Search</Hint>
          <Hint>F2 Customer</Hint>
          <Hint>F3 Hold</Hint>
          <Hint>F4 Recall</Hint>
          <Hint>F5/Space Pay</Hint>
          <Hint>F7 History</Hint>
          <Hint>F8 Void</Hint>
          <Hint>F9 Return</Hint>
          <Hint>F10 Exchange</Hint>
          <Hint>F11 Disc</Hint>
          <Hint>+/− Qty</Hint>
          <Hint>Del Remove</Hint>
          <Hint>Click qty/price/disc to edit</Hint>
        </HintBar>
      </RightPanel>

      {/* MOBILE-ONLY sticky nav — drives Products → Cart → Pay. Hidden on desktop. */}
      <MobileNavBar>
        {mobileStep === 'products' && (
          <MobileNavBtn $primary onClick={() => setMobileStep('cart')}>
            <FiShoppingCart size={18} />
            View Cart ({cart.length}) · {formatCurrency(exchangeMode ? Math.abs(exchangeNet) : totals.totalAmount)}
          </MobileNavBtn>
        )}
        {mobileStep === 'cart' && (
          <>
            <MobileBackBtn onClick={() => setMobileStep('products')} title="Back to products">
              <FiChevronLeft size={20} />
            </MobileBackBtn>
            <MobileNavBtn $primary disabled={cart.length === 0 && !exchangeMode} onClick={() => setMobileStep('pay')}>
              Pay {formatCurrency(exchangeMode ? Math.abs(exchangeNet) : totals.totalAmount)}
            </MobileNavBtn>
          </>
        )}
        {mobileStep === 'pay' && (
          <MobileBackBtn onClick={() => setMobileStep('cart')} style={{ flex: 1, justifyContent: 'center', gap: 6 }} title="Back to cart">
            <FiChevronLeft size={20} /> Back to Cart
          </MobileBackBtn>
        )}
      </MobileNavBar>

      {showScanner && (
        <POSCameraScanner
          onDetected={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <POSHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        companyId={companyId}
        activeBranchId={activeBranch?.id || null}
        activeCounterId={activeCounter?.id || null}
        onReprint={handleHistoryReprint}
        onCreateReturn={handleHistoryReturn}
        onVoid={handleHistoryVoid}
        onDelete={handleHistoryDelete}
      />

      <POSReceiptPickerModal
        isOpen={!!pickerMode}
        mode={pickerMode || 'return'}
        companyId={companyId}
        activeBranchId={activeBranch?.id || null}
        activeCounterId={activeCounter?.id || null}
        onSelect={handlePickerSelect}
        onClose={() => setPickerMode(null)}
      />

      {editTarget && (() => {
        const line = cart[editTarget.lineIndex];
        if (!line) return null;
        const twoItem = editTarget.field === 'qty2' ? itemsById[line.itemId] : null;
        const twoLineUnit = line.unitCode || line.unitOfMeasure;
        const twoSecond = twoItem ? secondUnitForLine(twoItem, twoLineUnit) : null;
        const cfg = editTarget.field === 'qty' ? {
          title: 'Quantity',
          initialValue: line.quantity || 0
        } : editTarget.field === 'qty2' ? {
          title: `Quantity in ${twoSecond || ''}`,
          initialValue: twoSecond ? secondQtyFromQty(twoItem, line.quantity, twoLineUnit, twoSecond) : 0
        } : editTarget.field === 'price' ? {
          title: 'Unit Price',
          initialValue: line.unitPrice || 0
        } : {
          title: 'Line Discount',
          initialValue: editTarget.mode === 'percent' ? (line.discountPercent || 0) : (line.discountAmount || 0),
          max: editTarget.mode === 'percent' ? 100 : null
        };
        return (
          <POSCalculatorModal
            open={true}
            title={cfg.title}
            subtitle={line.itemName}
            field={editTarget.field === 'qty2' ? 'qty' : editTarget.field}
            initialValue={cfg.initialValue}
            initialMode={editTarget.mode}
            max={cfg.max}
            onConfirm={handleEditConfirm}
            onClose={() => setEditTarget(null)}
          />
        );
      })()}

      {/* Open Shift modal */}
      {showOpenShift && activeCounter && (
        <ShiftOverlay>
          <ShiftCard>
            <ShiftTitle>Open Shift — {activeCounter.counterName}</ShiftTitle>
            <ShiftHint>Enter the cash currently in the drawer (opening float). Selling is blocked until a shift is open.</ShiftHint>
            <ShiftField>
              <label>Opening Cash</label>
              <ShiftInput
                type="number" min="0" step="0.01" value={openingCashInput}
                onChange={e => setOpeningCashInput(e.target.value)} autoFocus
              />
            </ShiftField>
            <ShiftBtnRow>
              <ShiftCancelBtn type="button" onClick={() => setShowOpenShift(false)}>Cancel</ShiftCancelBtn>
              <ShiftPrimaryBtn type="button" onClick={handleOpenShift} disabled={shiftBusy}>
                {shiftBusy ? 'Opening…' : 'Open Shift'}
              </ShiftPrimaryBtn>
            </ShiftBtnRow>
          </ShiftCard>
        </ShiftOverlay>
      )}

      {/* Close Shift modal */}
      {showCloseShift && closeTotals && activeShift && (() => {
        const counted = Number(countedCashInput) || 0;
        const variance = Math.round((counted - closeTotals.expectedCash) * 100) / 100;
        return (
          <ShiftOverlay>
            <ShiftCard>
              <ShiftTitle>Close Shift — {activeShift.shiftNumber}</ShiftTitle>
              <ShiftSummary>
                <ShiftRow><span>Opening Cash</span><b>{formatCurrency(activeShift.openingCash || 0)}</b></ShiftRow>
                <ShiftRow><span>Cash Sales ({closeTotals.saleCount})</span><b>{formatCurrency(closeTotals.cashSales)}</b></ShiftRow>
                <ShiftRow><span>Cash Refunds ({closeTotals.returnCount})</span><b>-{formatCurrency(closeTotals.cashReturns)}</b></ShiftRow>
                <ShiftRow><span>Card / Bank Sales</span><b>{formatCurrency(closeTotals.cardSales)}</b></ShiftRow>
                <ShiftRow><span>Credit Sales</span><b>{formatCurrency(closeTotals.creditSales)}</b></ShiftRow>
                <ShiftRow $total><span>Expected Cash in Drawer</span><b>{formatCurrency(closeTotals.expectedCash)}</b></ShiftRow>
              </ShiftSummary>
              <ShiftField>
                <label>Counted Cash</label>
                <ShiftInput
                  type="number" min="0" step="0.01" value={countedCashInput}
                  onChange={e => setCountedCashInput(e.target.value)} autoFocus
                />
              </ShiftField>
              <ShiftRow $total>
                <span>Variance (counted − expected)</span>
                <b style={{ color: Math.abs(variance) < 0.01 ? colors.success.main : colors.error.main }}>
                  {formatCurrency(variance)}
                </b>
              </ShiftRow>
              <ShiftField>
                <label>Notes (optional)</label>
                <ShiftInput
                  as="textarea" rows={2} value={closeNotes}
                  onChange={e => setCloseNotes(e.target.value)}
                  placeholder="Reason for variance, handover note, etc."
                />
              </ShiftField>
              <ShiftBtnRow>
                <ShiftCancelBtn type="button" onClick={() => setShowCloseShift(false)}>Cancel</ShiftCancelBtn>
                <ShiftPrimaryBtn type="button" onClick={handleConfirmCloseShift} disabled={shiftBusy}>
                  {shiftBusy ? 'Closing…' : 'Close & Print Z'}
                </ShiftPrimaryBtn>
              </ShiftBtnRow>
            </ShiftCard>
          </ShiftOverlay>
        );
      })()}

      {/* Quick-add / find customer modal — phone is the unique key */}
      {showAddCustomer && (
        <ShiftOverlay>
          <ShiftCard>
            <ShiftTitle>Add / Find Customer</ShiftTitle>
            <ShiftHint>Enter the phone number. If it already exists, that customer is selected — otherwise add a name.</ShiftHint>
            <ShiftField>
              <label>Phone Number</label>
              <ShiftInput
                type="tel" value={newCustPhone}
                onChange={e => setNewCustPhone(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && matchedCustomer) handleQuickAddCustomer(); }}
                placeholder="03XX-XXXXXXX" autoFocus
              />
            </ShiftField>
            {matchedCustomer ? (
              <ExistingCustNote>
                Existing customer: <b>{matchedCustomer.companyName || matchedCustomer.customerName}</b>
              </ExistingCustNote>
            ) : normalizePhone(newCustPhone).length >= 4 ? (
              <ShiftField>
                <label>Name (new customer)</label>
                <ShiftInput
                  type="text" value={newCustName}
                  onChange={e => setNewCustName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleQuickAddCustomer(); }}
                  placeholder="Customer name"
                />
              </ShiftField>
            ) : null}
            <ShiftBtnRow>
              <ShiftCancelBtn type="button" onClick={() => { setShowAddCustomer(false); setNewCustName(''); setNewCustPhone(''); }}>
                Cancel
              </ShiftCancelBtn>
              <ShiftPrimaryBtn type="button" onClick={handleQuickAddCustomer} disabled={addingCustomer || normalizePhone(newCustPhone).length < 4}>
                {addingCustomer ? 'Saving…' : matchedCustomer ? 'Select' : 'Add & Select'}
              </ShiftPrimaryBtn>
            </ShiftBtnRow>
          </ShiftCard>
        </ShiftOverlay>
      )}

      {/* Delete bill — admin password approval */}
      {deleteBillTarget && (
        <ShiftOverlay>
          <ShiftCard>
            <ShiftTitle>Delete Bill {deleteBillTarget.docNumber}</ShiftTitle>
            <ShiftHint>
              This permanently removes the bill and reverses its stock & accounting. Enter the
              <b> admin password</b> to approve.
            </ShiftHint>
            <ShiftField>
              <label>Admin Password</label>
              <ShiftInput
                type="password" value={deletePassword}
                onChange={e => setDeletePassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmDeleteBill(); }}
                autoFocus
              />
            </ShiftField>
            <ShiftBtnRow>
              <ShiftCancelBtn type="button" onClick={() => { setDeleteBillTarget(null); setDeletePassword(''); }}>
                Cancel
              </ShiftCancelBtn>
              <ShiftPrimaryBtn type="button" onClick={confirmDeleteBill} disabled={deleteBusy} style={{ background: colors.error.main }}>
                {deleteBusy ? 'Deleting…' : 'Delete Bill'}
              </ShiftPrimaryBtn>
            </ShiftBtnRow>
          </ShiftCard>
        </ShiftOverlay>
      )}

      {/* POS-01: a printed sale whose save needs attention — must be acknowledged */}
      {saveAlerts.length > 0 && (
        <ShiftOverlay>
          <ShiftCard>
            <ShiftTitle>{saveAlerts[0].title}</ShiftTitle>
            <ShiftHint>{saveAlerts[0].text}</ShiftHint>
            <ShiftBtnRow>
              <ShiftPrimaryBtn type="button" autoFocus onClick={() => setSaveAlerts(prev => prev.slice(1))}>
                OK{saveAlerts.length > 1 ? ` (${saveAlerts.length - 1} more)` : ''}
              </ShiftPrimaryBtn>
            </ShiftBtnRow>
          </ShiftCard>
        </ShiftOverlay>
      )}

      {toast && (
        <Toast $tone={toast.tone}>{toast.msg}</Toast>
      )}
    </FullScreen>
  );
};

// ─── Styled ───────────────────────────────────────────────────────────────

// Below this width the POS switches from the desktop 3-panel view to the
// single-column, step-based mobile flow. Every mobile rule is guarded by this
// query, so the desktop layout is byte-for-byte unchanged.
const MOBILE = '@media (max-width: 820px)';

const FullScreen = styled.div`
  position: fixed; inset: 0;
  display: flex;
  background: ${colors.background.paper};
  z-index: 999;
  font-family: ${typography.fontFamily.primary};
  ${MOBILE} {
    flex-direction: column;
    overflow-y: auto;
    padding-bottom: 80px;   /* room for the sticky mobile nav bar */
  }
`;
const LoadingScreen = styled.div`
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${colors.background.paper}; z-index: 999;
`;
const LoadingText = styled.div`
  font-size: ${typography.fontSize.xl};
  color: ${colors.text.secondary};
`;
const LeftPanel = styled.div`
  width: 48%;
  display: flex; flex-direction: column;
  border-right: 2px solid ${colors.border.light};
  background: white;
  overflow: hidden;
  ${MOBILE} {
    width: 100%;
    border-right: none;
    overflow: visible;
    flex: none;
  }
`;
const TopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px;
  background: ${colors.secondary.main};
  color: white; flex-shrink: 0;
  ${MOBILE} { flex-wrap: wrap; gap: 8px; padding: 8px 12px; }
`;
const TopBarLeft = styled.div` display: flex; align-items: center; gap: 16px; `;
const CounterTitle = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;
const LogoText = styled.span`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 1px;
`;
const BranchText = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  opacity: 0.75;
`;
const ClockText = styled.span`
  font-size: ${typography.fontSize.sm};
  opacity: 0.7;
`;
const ShiftBadge = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 3px 4px 3px 10px;
  background: rgba(255,255,255,0.12);
  border-radius: ${borderRadius.md};
`;
const ShiftBadgeText = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
`;
const ShiftActionBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 5px 12px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  color: white;
  background: ${props => props.$open ? colors.success.main : colors.error.main};
  border: none; border-radius: ${borderRadius.sm};
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const ShiftOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1200;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
`;
const ShiftCard = styled.div`
  background: white; border-radius: ${borderRadius.lg};
  width: 100%; max-width: 420px;
  padding: 22px 24px;
  box-shadow: ${shadows.lg};
`;
const ShiftTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;
const ShiftHint = styled.p`
  margin: 0 0 16px 0;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
`;
const ShiftField = styled.div`
  margin: 12px 0;
  label {
    display: block; margin-bottom: 6px;
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.primary};
  }
`;
const ShiftInput = styled.input`
  width: 100%; padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.md};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const ShiftSummary = styled.div`
  background: ${colors.neutral[50]};
  border-radius: ${borderRadius.md};
  padding: 10px 14px; margin-bottom: 8px;
`;
const ShiftRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 5px 0;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  ${props => props.$total && `
    border-top: 1px dashed ${colors.neutral[300]};
    margin-top: 4px; padding-top: 8px;
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.text.primary};
    font-size: ${typography.fontSize.md};
  `}
  b { color: ${colors.text.primary}; }
`;
const ShiftBtnRow = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px;
`;
const ShiftCancelBtn = styled.button`
  padding: 10px 18px; background: white;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary}; cursor: pointer;
`;
const ShiftPrimaryBtn = styled.button`
  padding: 10px 18px; background: ${colors.primary.main};
  border: none; border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.bold};
  color: white; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const TopBarRight = styled.div` display: flex; align-items: center; gap: 6px; position: relative; `;
const PriceTypeWrap = styled.div`
  display: flex; align-items: center; gap: 5px;
  padding: 3px 8px;
  border-radius: ${borderRadius.sm};
  border: 1px solid rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.1);
`;
const PriceTypeLabel = styled.span`
  font-size: 10px; font-weight: ${typography.fontWeight.semibold};
  color: rgba(255,255,255,0.85); text-transform: uppercase; letter-spacing: 0.4px;
`;
const PriceTypeSelect = styled.select`
  border: none; outline: none; cursor: pointer;
  background: white; color: ${colors.text.primary};
  border-radius: 4px; padding: 3px 6px;
  font-size: 11px; font-weight: ${typography.fontWeight.semibold};
`;
const TopBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 5px 10px;
  border-radius: ${borderRadius.sm};
  font-size: 11px; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.3);
  background: transparent; color: white;
  &:hover:not(:disabled) { background: rgba(255,255,255,0.15); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const ParkedWrap = styled.div` position: relative; `;
const ParkedDropdown = styled.div`
  position: absolute; top: calc(100% + 4px); right: 0;
  width: 280px; max-height: 320px; overflow-y: auto;
  background: white; color: ${colors.text.primary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};
  z-index: 50;
`;
const ParkedEmpty = styled.div`
  padding: 16px;
  text-align: center; color: ${colors.text.tertiary};
  font-size: ${typography.fontSize.xs};
`;
const ParkedItem = styled.div`
  padding: 8px 12px;
  display: flex; justify-content: space-between; align-items: center;
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
  small { color: ${colors.text.tertiary}; font-size: 10px; }
`;
const ReturnToggle = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 5px 10px;
  border-radius: ${borderRadius.sm};
  font-size: 11px; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.3);
  background: ${({ $active }) => $active ? colors.error.main : 'transparent'};
  color: white;
  &:hover { background: ${colors.error.main}; }
`;
const ExitButton = styled.button`
  display: flex; align-items: center; justify-content: center;
  padding: 6px;
  border-radius: ${borderRadius.sm};
  background: transparent; color: white;
  border: 1px solid rgba(255,255,255,0.3);
  cursor: pointer;
  &:hover { background: ${colors.error.main}; }
`;
const ReturnBanner = styled.div`
  background: ${colors.error.main}; color: white;
  text-align: center; padding: 6px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 1px;
  flex-shrink: 0;
`;
const ExchangeBanner = styled.div`
  background: #7C3AED; color: white;
  text-align: center; padding: 6px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
  flex-shrink: 0;
`;
const MidArea = styled.div`
  /* Shared scrollable container for Exchange panel + Cart. When exchange mode
     is on, both panels share this vertical space; when it's off, only the
     cart sits here and gets all of it. min-height: 0 lets flex shrink the
     contents past their intrinsic size so short screens still show the cart. */
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  ${MOBILE} {
    display: ${p => (p.$mstep === 'cart' ? 'flex' : 'none')};
    flex: none;
    overflow: visible;
  }
`;
const ExchangePanel = styled.div`
  background: #FAF5FF;
  border-bottom: 1px solid #E9D5FF;
  display: flex; flex-direction: column;
  /* Bigger panel so multiple return lines + codes/names are readable
     without aggressive scrolling. Capped so cart still shows below. */
  flex: 0 1 auto;
  max-height: 55%;
  min-height: 200px;
`;
const ExchangePanelHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 12px;
  background: #F3E8FF;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: #5B21B6;
`;
const ReasonSelect = styled.select`
  padding: 4px 8px;
  font-size: ${typography.fontSize.xs};
  border: 1px solid #E9D5FF;
  border-radius: ${borderRadius.sm};
  background: white;
  outline: none;
  color: #5B21B6;
`;
const ExchangeScroll = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #DDD6FE; border-radius: 4px; }
`;
const ExchangeRow = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr 70px 90px;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid #F3E8FF;
  background: ${({ $selected }) => $selected ? '#F5F3FF' : 'transparent'};
  font-size: ${typography.fontSize.xs};
`;
const ExName = styled.div`
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeight.medium};
  small { display: block; color: ${colors.text.tertiary}; font-size: 10px; font-weight: 400; }
`;
const ExCode = styled.span`
  display: inline-block;
  background: #EDE9FE;
  color: #5B21B6;
  font-family: monospace;
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  padding: 1px 6px;
  border-radius: 3px;
  margin-right: 6px;
`;
const ExQty = styled.input`
  padding: 4px 6px;
  border: 1px solid #DDD6FE;
  border-radius: 4px;
  text-align: right;
  font-size: ${typography.fontSize.xs};
  &:disabled { background: ${colors.neutral[50]}; color: ${colors.text.tertiary}; }
`;
const ExTotal = styled.div`
  text-align: right;
  font-weight: ${typography.fontWeight.semibold};
  color: #B45309;
`;
const ExchangeSummary = styled.div`
  display: flex; justify-content: space-between;
  padding: 6px 12px;
  background: #EDE9FE;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  color: #5B21B6;
`;
const RefundHint = styled.div`
  padding: 6px 12px;
  background: #FEF3C7; color: #92400E;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  text-align: center;
`;
const CustomerBar = styled.div`
  display: flex; align-items: center;
  padding: 6px 12px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.border.light};
  flex-shrink: 0; position: relative; gap: 6px;
  ${MOBILE} {
    display: ${p => (p.$mstep === 'products' || p.$mstep === 'cart' ? 'flex' : 'none')};
  }
`;
const CustomerDisplay = styled.button`
  display: flex; align-items: center; gap: 8px;
  flex: 1;
  padding: 6px 10px;
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  text-align: left;
  &:hover { border-color: ${colors.primary.main}; }
`;
const CustomerNameText = styled.span`
  flex: 1; font-weight: ${typography.fontWeight.medium};
  color: ${({ $isWalkIn }) => $isWalkIn ? colors.text.secondary : colors.primary.main};
`;
const ResetCustomerBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  background: white; color: ${colors.text.secondary};
  cursor: pointer;
  &:hover { color: ${colors.error.main}; border-color: ${colors.error.main}; }
`;
const CustomerDropdown = styled.div`
  position: absolute; top: 100%; left: 12px; right: 12px;
  background: white;
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};
  z-index: 50; max-height: 280px;
  display: flex; flex-direction: column;
`;
const CustomerSearchInput = styled.input`
  padding: 10px 12px; border: none;
  border-bottom: 1px solid ${colors.border.light};
  font-size: ${typography.fontSize.sm}; outline: none;
`;
const AddCustomerBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 5px;
  height: 28px; padding: 0 12px;
  border: 1px solid ${colors.primary.main};
  border-radius: ${borderRadius.sm};
  background: ${colors.primary.main}12;
  color: ${colors.primary.main};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer; white-space: nowrap;
  &:hover { background: ${colors.primary.main}22; }
`;
const ExistingCustNote = styled.div`
  margin: -4px 0 8px;
  padding: 10px 12px;
  background: ${colors.success.main}12;
  border: 1px solid ${colors.success.main}40;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  b { color: ${colors.success.main}; }
`;
const CustomerList = styled.div`
  overflow-y: auto; max-height: 220px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 4px; }
`;
const CustomerOption = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 12px; cursor: pointer;
  background: ${({ $active }) => $active ? colors.primary.light + '20' : 'transparent'};
  &:hover { background: ${colors.neutral[50]}; }
  & + & { border-top: 1px solid ${colors.neutral[100]}; }
`;
const CustomerOptName = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
`;
const CustomerOptTag = styled.span`
  font-size: 10px; color: ${colors.text.tertiary};
  background: ${colors.neutral[100]};
  padding: 2px 6px; border-radius: 4px;
`;
const CartContainer = styled.div`
  /* Always takes the remaining MidArea space, never shrinks below ~100px so
     the cashier can always see a few rows even when the Exchange panel is
     open on a 720p screen. */
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 100px;
`;
const CartHeader = styled.div`
  display: flex; padding: 8px 12px;
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.border.light};
  flex-shrink: 0;
`;
const CartHeaderCell = styled.div`
  flex: ${({ $flex }) => $flex || 1};
  font-size: 10px; font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;
const CartScrollArea = styled.div`
  flex: 1; overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 4px; }
`;
const EmptyCart = styled.div`
  display: flex; align-items: center; justify-content: center;
  height: 100%;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSize.sm};
  padding: 40px;
`;
const CartRow = styled.div`
  display: flex; align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid ${colors.neutral[100]};
  cursor: pointer;
  transition: background 0.1s;
  background: ${({ $selected }) => $selected ? '#FFF3F5' : 'transparent'};
  &:hover { background: ${({ $selected }) => $selected ? '#FFF3F5' : colors.neutral[50]}; }
`;
const CartCell = styled.div`
  flex: ${({ $flex }) => $flex || 1};
  font-size: ${typography.fontSize.sm};
`;
const ItemName = styled.div`
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
  font-size: ${typography.fontSize.sm};
  line-height: 1.3;
`;
const AuditLockBar = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 9999;
  background: #C62828;
  color: #fff;
  text-align: center;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;
const CartBarcode = styled.div`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  line-height: 1.3;
  margin-top: 1px;
`;
const QtyControls = styled.div` display: flex; align-items: center; gap: 4px; `;
const QtyBtn = styled.button`
  width: 24px; height: 24px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid ${colors.border.light};
  border-radius: 4px; background: white; cursor: pointer; padding: 0;
  &:hover { background: ${colors.neutral[100]}; }
`;
const QtyValueBtn = styled.button`
  font-family: inherit;
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.sm};
  min-width: 32px; padding: 2px 6px;
  background: white;
  border: 1px dashed ${colors.border.light};
  border-radius: 4px;
  color: ${colors.text.primary};
  cursor: pointer;
  &:hover { background: ${colors.primary.main}15; border-color: ${colors.primary.main}; }
  &:active { transform: scale(0.96); }
`;
const PriceBtn = styled.button`
  font-family: inherit;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  background: white;
  border: 1px dashed ${colors.border.light};
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  &:hover { background: ${colors.primary.main}15; border-color: ${colors.primary.main}; color: ${colors.text.primary}; }
  &:active { transform: scale(0.96); }
`;
const DiscChip = styled.button`
  margin-top: 3px;
  display: inline-block;
  font-family: inherit;
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px dashed ${({ $hasDisc }) => $hasDisc ? colors.error.main : colors.border.light};
  background: ${({ $hasDisc }) => $hasDisc ? '#FFEBEE' : 'transparent'};
  color: ${({ $hasDisc }) => $hasDisc ? colors.error.main : colors.text.tertiary};
  &:hover {
    background: ${({ $hasDisc }) => $hasDisc ? '#FFCDD2' : colors.primary.main + '15'};
    border-color: ${({ $hasDisc }) => $hasDisc ? colors.error.main : colors.primary.main};
    color: ${({ $hasDisc }) => $hasDisc ? colors.error.main : colors.primary.main};
  }
`;
const TwoUnitChip = styled.button`
  display: block; margin-top: 3px; padding: 1px 6px;
  font-family: inherit; font-size: 11px; font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.secondary}; background: ${colors.neutral[50]};
  border: 1px dashed ${colors.border.light}; border-radius: 4px; cursor: pointer;
  white-space: nowrap;
  &:hover { background: ${colors.primary.main}15; border-color: ${colors.primary.main}; color: ${colors.text.primary}; }
`;
const UnitPicker = styled.select`
  margin-top: 2px; padding: 1px 3px;
  font-size: 10px;
  border: 1px solid ${colors.border.light};
  border-radius: 3px; background: white;
  color: ${colors.text.secondary}; cursor: pointer;
  max-width: 60px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const LinePriceType = styled.select`
  margin-top: 3px; padding: 1px 3px;
  font-size: 10px; font-weight: ${typography.fontWeight.medium};
  border: 1px solid ${colors.primary.main}40;
  border-radius: 3px; background: white;
  color: ${colors.primary.main}; cursor: pointer;
  max-width: 74px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const PriceText = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
`;
const TotalText = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  font-size: ${typography.fontSize.sm};
`;
const RemoveItemBtn = styled.button`
  background: none; border: none;
  color: ${colors.text.tertiary};
  cursor: pointer; padding: 2px;
  &:hover { color: ${colors.error.main}; }
`;
const TotalsSection = styled.div`
  padding: 10px 16px;
  border-top: 2px solid ${colors.border.light};
  background: ${colors.neutral[50]};
  flex-shrink: 0;
  ${MOBILE} {
    display: ${p => (p.$mstep === 'cart' || p.$mstep === 'pay' ? 'block' : 'none')};
  }
`;
const TotalRow = styled.div`
  display: flex; justify-content: space-between;
  font-size: ${typography.fontSize.sm};
  color: ${({ $muted }) => $muted ? colors.text.secondary : colors.text.primary};
  margin-bottom: 2px;
`;
const GrandTotalRow = styled.div`
  display: flex; justify-content: space-between;
  font-size: ${typography.fontSize.xxl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary.main};
  margin-top: 4px; padding-top: 6px;
  border-top: 2px solid ${colors.primary.main};
`;
const ItemCountRow = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.tertiary};
  text-align: center; margin-top: 2px;
`;
const ActionRail = styled.div`
  /* Slim vertical column of secondary actions between the cart and the
     product grid. Moves Void/Disc/Reprint/Repeat off the left panel so the
     cart and Exchange list can use that vertical space. */
  flex: 0 0 72px;
  display: flex; flex-direction: column;
  gap: 4px;
  padding: 8px 6px;
  background: ${colors.neutral[50]};
  border-left: 1px solid ${colors.border.light};
  ${MOBILE} {
    /* On mobile the rail becomes a horizontal cart-actions bar, shown only on
       the Cart step (Void / Disc / Hold / History). */
    display: ${p => (p.$mstep === 'cart' ? 'flex' : 'none')};
    flex: none;
    flex-direction: row;
    width: 100%;
    justify-content: space-around;
    border-left: none;
    border-top: 1px solid ${colors.border.light};
  }
  border-right: 1px solid ${colors.border.light};
  overflow-y: auto;
`;
const RailBtn = styled.button`
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 4px;
  padding: 10px 4px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: all 0.1s;
  &:hover:not(:disabled) {
    background: ${colors.primary.main}10;
    border-color: ${colors.primary.main};
    color: ${colors.primary.main};
  }
  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;
const RailLbl = styled.span`
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
`;
const RailSub = styled.span`
  font-size: 9px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.warning.main};
  line-height: 1;
`;
const RightPanel = styled.div`
  flex: 1;
  display: flex; flex-direction: column;
  padding: 12px; gap: 8px;
  min-height: 0; overflow: hidden;
  ${MOBILE} {
    display: ${p => (p.$mstep === 'products' ? 'flex' : 'none')};
    width: 100%;
    min-height: 60vh;
    overflow: visible;
  }
`;
const SearchBar = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  background: white;
  border: 2px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  flex: 0 0 auto;
  &:focus-within { border-color: ${colors.primary.main}; }
  color: ${colors.text.secondary};
`;
const SearchInput = styled.input`
  flex: 1; border: none; outline: none;
  font-size: ${typography.fontSize.md};
  &::placeholder { color: ${colors.text.tertiary}; }
`;
const ClearSearch = styled.button`
  background: none; border: none;
  color: ${colors.text.tertiary};
  cursor: pointer; padding: 2px;
  &:hover { color: ${colors.text.primary}; }
`;
const ProductGridWrapper = styled.div`
  flex: 1 1 0%; min-height: 0; overflow: hidden;
  display: flex; flex-direction: column;
  ${MOBILE} {
    overflow: visible;
    min-height: 55vh;
  }
`;
const HintBar = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px;
  padding: 4px 0 0;
  font-size: 10px;
  flex: 0 0 auto;
  ${MOBILE} { display: none; }   /* keyboard F-key hints are useless on a phone */
`;

// ─── Mobile-only chrome (all hidden on desktop) ─────────────────────────────
// Sticky bottom navigation that drives the Products → Cart → Pay flow.
const MobileNavBar = styled.div`
  display: none;
  ${MOBILE} {
    display: flex; align-items: stretch; gap: 8px;
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 1200;
    padding: 8px 10px;
    background: #fff;
    border-top: 1px solid ${colors.border.light};
    box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
  }
`;
const MobileNavBtn = styled.button`
  flex: 1;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  border: none; border-radius: 10px; cursor: pointer;
  padding: 12px 10px; font-size: 15px; font-weight: 700;
  background: ${p => (p.$primary ? colors.secondary.main : colors.neutral[100])};
  color: ${p => (p.$primary ? '#fff' : colors.text.primary)};
  &:disabled { opacity: 0.5; }
`;
const MobileBackBtn = styled.button`
  flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid ${colors.border.light}; border-radius: 10px; cursor: pointer;
  padding: 12px 14px; background: #fff; color: ${colors.text.primary};
`;
// "More" trigger in the top bar (mobile only) + the collapsible sheet that holds
// the session controls (price / recall / return / exchange). Reuses the exact
// same control components — they just relocate here on mobile.
const MoreBtn = styled.button`
  display: none;
  ${MOBILE} {
    display: inline-flex; align-items: center; gap: 4px;
    background: rgba(255,255,255,0.16); color: #fff;
    border: none; border-radius: 6px; cursor: pointer;
    padding: 6px 10px; font-size: 12px; font-weight: 600;
  }
`;
const MoreBackdrop = styled.div`
  display: none;
  ${MOBILE} {
    display: ${p => (p.$open ? 'block' : 'none')};
    position: fixed; inset: 0; z-index: 1490;
    background: rgba(0,0,0,0.35);
  }
`;
const SecondaryControls = styled.div`
  display: contents;   /* desktop: children sit inline in the top bar as before */
  ${MOBILE} {
    /* Mobile: a bottom sheet (full-width, can't run off-screen like the old
       top-anchored dropdown did). */
    display: ${p => (p.$open ? 'flex' : 'none')};
    position: fixed; left: 0; right: 0; bottom: 0; top: auto; z-index: 1500;
    flex-direction: column; align-items: stretch; gap: 10px;
    background: #fff; color: ${colors.text.primary};
    border: none; border-radius: 16px 16px 0 0;
    box-shadow: 0 -8px 30px rgba(0,0,0,0.22);
    padding: 16px 16px 22px; min-width: 0;
    max-height: 72vh; overflow-y: auto;
    &::before {
      content: 'More actions';
      font-weight: 700; color: ${colors.secondary.main};
      font-size: 15px; padding-bottom: 4px;
    }

    /* These controls are styled white-on-maroon for the dark top bar. Inside the
       light dropdown that makes them invisible — recolor to dark-on-light and
       stretch them to full-width list rows. */
    ${PriceTypeWrap}, ${ParkedWrap} { width: 100%; }
    ${PriceTypeLabel} { color: ${colors.text.secondary}; }
    ${PriceTypeSelect} {
      color: ${colors.text.primary}; background: #fff;
      border: 1px solid ${colors.border.light}; width: 100%; padding: 8px 10px;
    }
    ${TopBtn}, ${ReturnToggle} {
      color: ${colors.text.primary};
      background: ${colors.neutral[100]};
      border: 1px solid ${colors.border.light};
      width: 100%; justify-content: flex-start;
      padding: 10px 12px; font-size: 14px;
    }
  }
`;
// Wraps the imported PaymentBar so mobile can show it only on the Pay step.
const MobilePayWrap = styled.div`
  display: contents;
  ${MOBILE} {
    display: ${p => (p.$mstep === 'pay' ? 'block' : 'none')};
  }
`;
const ScanBtn = styled.button`
  display: none;
  ${MOBILE} {
    display: inline-flex; align-items: center; justify-content: center;
    background: ${colors.secondary.main}; color: #fff;
    border: none; border-radius: 8px; cursor: pointer;
    width: 40px; height: 40px; flex: 0 0 auto;
  }
`;
const Hint = styled.span`
  padding: 2px 6px;
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
  border-radius: 4px;
  font-weight: ${typography.fontWeight.semibold};
`;
const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;
const Toast = styled.div`
  position: fixed; bottom: 24px; left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  background: ${({ $tone }) =>
    $tone === 'error' ? colors.error.main :
    $tone === 'success' ? colors.success.main :
    colors.secondary.main};
  color: white;
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  z-index: 1100;
  animation: ${slideIn} 0.2s ease-out;
  max-width: 600px;
`;

export default POSScreen;
